/**
 * Schémas Zod du contenu — recopiés de SPEC-CONTENU §2 (types de base) et §3 (schémas par
 * fichier). Zod v3.
 *
 * Règle absolue (SPEC-CONTENU §1.3) : un fichier de `content/` ne peut utiliser QUE les
 * 18 types d'effets de l'enum fermé du §2.3. `discriminatedUnion` + `.strict()` partout ⇒
 * un `type` inventé ou une clé inconnue font ÉCHOUER le parse (test T17).
 *
 * Deux ajouts imposés par le BACKLOG (arbitrages déjà tranchés par la table A1-A24) :
 *  - **A9** : `GameEvent` reçoit les champs optionnels `cooldown_h`, `major`, `window`
 *    attendus par SPEC-MOTEUR §10 (§10.1 pas 2 et 9, §10.3, §10.5).
 *  - **A11** : `content/` compte **7 fichiers** (meta + les 5 + `atelier.json`) ; le schéma
 *    `AtelierFile` est défini ici (le FICHIER lui-même est produit en M2.8).
 *
 * Le loader (`load.ts`, M1.4) parse les fichiers au boot du serveur ; tout échec de parse =
 * le conteneur refuse de démarrer (PLAN §6, SPEC-API §8.2).
 */
import { z } from "zod";
// ─────────────────────────────────────────────────────────────────────────────
// §2.1 Enums de base
// ─────────────────────────────────────────────────────────────────────────────
/** Id snake_case ASCII. */
export const Id = z.string().regex(/^[a-z0-9_]+$/);
/** Jauges modifiables par un effet gauge_delta.
 *  « abonnes » est EXCLU : les abonnés ne bougent que via subs_pct / subs_flat
 *  (plafond du PLAN §4.9 vérifiable).
 *
 *  ⟦SPEC-FIL §8.3 · M3.22⟧ **`hype` en fait partie depuis le branchement du Fil.** L'encadré
 *  qui tenait ici disait « pas encore », et il avait raison de le dire : ⟦A-57⟧ avait refusé
 *  de l'ajouter tant qu'`engine/effects.ts` ne savait pas l'appliquer, parce qu'un
 *  `gauge_delta` sur une jauge que le moteur ignore est un **effet silencieusement perdu** —
 *  le contraire d'un état neutre. `effects.ts` sait, maintenant : c'est ce qui permet à un
 *  événement, un live ou un Q&A de contenu de bouger la Hype **sans une ligne de moteur**. */
export const GaugeId = z.enum([
    "energie", // 0-100
    "sante_mentale", // 0-100
    "reputation", // 0-100
    "fidelite", // 0-100
    "argent", // euros, non borné
    "momentum", // 0.8-1.5
    "inspiration", // 0-6 cartes
    "hype", // 0-100 ⟦SPEC-FIL §2.1⟧
]);
/** Les 6 branches de l'arbre (PLAN §4.12). */
export const BranchId = z.enum([
    "tournage",
    "montage",
    "ecriture",
    "charisme",
    "business",
    "algorithme",
]);
/** Les 5 mini-jeux de l'Atelier (PLAN §4.5). */
export const MinigameId = z.enum([
    "fil_rouge", // Écriture « Le script qui tombe » (⟦C52⟧ ; id d'avant, gardé : les assists s'y accrochent)
    "bonne_prise", // Tournage « La prise parfaite » (⟦C53⟧ ; l'id garde son ancien nom, l'enum est fermé)
    "couper_rythme", // Montage « Couper dans le rythme »
    "putaclic_o_metre", // Miniature + Titre « Le putaclic-o-mètre »
    "la_decoupe", // Promotion « La Découpe » (longues seulement)
]);
/** Facteurs de la formule (PLAN §4.4) ciblés par potential_add. */
export const FactorId = z.enum(["accroche", "qualite", "diffusion", "portee"]);
/** Cibles de l'effet multiplier — où le moteur applique le facteur :
 *  xp         → XP gagnée (niveaux de chaîne → PC)
 *  rente      → rente hebdomadaire evergreen (§4.6)
 *  rpm        → revenus pub par 1 000 vues
 *  vues       → VuesPic après calcul complet
 *  conversion → TauxConversion (0,1 % → 0,6 %)
 *  revenus    → TOUS les revenus (rpm + sponso + merch + rente €)
 *  accroche / qualite / diffusion → le facteur EFFECTIF (après mini-jeu) */
export const MultiplierTarget = z.enum([
    "xp",
    "rente",
    "rpm",
    "vues",
    "conversion",
    "revenus",
    "accroche",
    "qualite",
    "diffusion",
]);
/** Capacités déblocables — enum FERMÉ, chaque capacité est implémentée par le moteur
 *  et débloquée par EXACTEMENT UN nœud (test T16). */
export const CapabilityId = z.enum([
    "production_evergreen", // produire une vidéo classe Evergreen (PLAN §4.6, nœud L'Intemporelle)
    "menage_bibliotheque", // passer une nulle en privé (PLAN §4.6, nœud Gestion d'image)
    "series_bibliotheque", // ranger en série, +20 % rente croisée (PLAN §4.6, nœud Lore de chaîne)
    "delegation_montage", // sauter le mini-jeu Montage contre € (PLAN §4.5, nœud Monteur salarié)
    "delegation_totale", // sauter n'importe quel mini-jeu contre € (PLAN §4.5, nœud Le Studio)
    "live", // action Live (v1 : action simple, PLAN §5)
    "potentiel_evergreen_visible", // révèle le potentiel evergreen des sujets (PLAN §4.12, nœud SEO)
    "phase_tendance_visible", // révèle phase + durée restante des tendances (PLAN §4.8, nœud Analytics)
    /**
     * ⟦SPEC-PRODUCTEUR §2.1 · A-66⟧ **Le deuxième étage de la délégation** : quelqu'un décide
     * quoi tourner. Ouvre `laisser_tourner` et `regler_equipe` (nœud *Le producteur*).
     *
     * ⚠️ La capacité **n'est pas** « produire une vidéo à ma place » : elle n'ouvre aucun chemin
     * de résolution. Le producteur n'émet que des `Action` existantes, qui repassent par
     * `resolve` — c'est ce qui garantit qu'aucune ancre ne peut bouger ⟦§0.4⟧.
     */
    "production_deleguee",
    /**
     * ⟦SPEC-PRODUCTEUR §2.1 · M3.29⟧ **Le troisième étage de la délégation** : quelqu'un tient
     * le rythme — dormir, veiller, souffler, tenir la caisse (nœud *Le manager*, gardé à
     * 100 000 abonnés par `abonnes_min`, prérequis `le_producteur` : l'escalier est littéral).
     *
     * ⚠️ Ce que les 16 PC achètent, dit exactement (§5.4bis) : pas une action de plus, pas une
     * journée de plus — **deux motifs d'arrêt en moins** (`sante_mentale`, `tresorerie`).
     * `evenement` reste inabsorbable, à cet étage comme aux autres — décision de Mathieu.
     */
    "rythme_delegue",
    /**
     * ⟦A-44 · A-71⟧ **Le guide MeTube** — ouvre l'écran du faux journal (nœud *Le guide MeTube*,
     * gardé à 3 000 abonnés par `abonnes_min`).
     *
     * ⚠️ C'est la seule capacité du jeu qui **n'ouvre aucune action** : elle ne change ni un coût,
     * ni un tirage, ni une résolution — elle ouvre une porte d'interface, et c'est tout. C'est le
     * sens même d'⟦A-44⟧ : *« dépenser des points de compétence pour SAVOIR au lieu de dépenser
     * pour FAIRE »*. Le moteur la stocke comme les autres et n'en fait rien, ce qui garantit
     * l'état neutre ⟦A-10⟧ par construction plutôt que par mesure.
     */
    "guide_metube",
    /**
     * ⟦SPEC-FIL §4.2 · §9.5 / A-82⟧ **Cœur sur vous** — les réponses `coeur` du Fil ont +50 %
     * d'effet (PLAN §4.14), et `annoncer_pause` devient découvrable par l'arbre ⟦A-2 route 2⟧.
     *
     * 🔑 **Une capacité, pas un nœud.** L'arbre est FERMÉ à 39 nœuds / 195 PC ⟦A-21⟧ ; le moteur
     * testait `state.skills.includes("coeur_sur_vous")`, une condition qui ne pouvait jamais être
     * vraie. Le patron ⟦A-56⟧ : l'effet entre par un nœud EXISTANT (*Le live*, branche Charisme)
     * et le moteur lit la capacité — jamais l'id du nœud.
     */
    "coeur_sur_vous",
    /**
     * ⟦SPEC-FIL §9.5 / A-82⟧ **Le community manager salarié** — la délégation du Fil : la nuit,
     * contre un salaire pro rata, il répond `coeur` aux commentaires de routine, à effet ÷2. Il
     * ne touche jamais une star, un cadeau, un toxique, un épinglé — ce qui décide reste au
     * joueur ⟦contrainte du 18/08⟧, et le joueur l'arrête quand il veut (`regler_cm`). Portée
     * par *Le Studio* (Business, après le Grand Saut ⟦A-29⟧ : c'est de l'argent).
     */
    "community_manager",
    /**
     * ⟦A-50 §3 levier 1 · A-108⟧ **« Je gère »** — le droit de jouer l'action `reporter_evenement`
     * (`types.ts`) sur un `pending_event` : au lieu de trancher, le joueur subit l'issue par
     * défaut (le premier `choix` de l'événement) et avance. Coûte, mais ne bloque plus.
     *
     * 🔑 **Même patron qu'⟦A-56 / A-82⟧** : l'arbre est FERMÉ à 41 nœuds ⟦A-85⟧, donc pas de nœud
     * neuf — la capacité est portée par *Gestion de crise* (Charisme, t5), un nœud dont les
     * `effets` étaient vides (sa seule mécanique passait par des `skill_requis`/`contremesure`
     * lus par id) : « laisser retomber, répondre posé » est exactement l'esprit de reporter une
     * réaction plutôt que de la précipiter — le même nœud qui apprend déjà à gérer une crise
     * apprend maintenant à en repousser une.
     */
    "reporter_evenement",
]);
/** Actions ciblables par action_cost_mult (sous-ensemble des actions du moteur). */
export const ActionId = z.enum([
    "short",
    "longue",
    "veille",
    "lire_commentaires",
    "menage",
    "remaster",
    "dormir",
]);
/** Ressources ciblables par action_cost_mult. */
export const ResourceId = z.enum(["heures", "energie", "sante_mentale", "argent"]);
/** Thèmes de sujets — enum fermé, référencé par subjects et trends. ✎ (liste v1) */
export const ThemeId = z.enum([
    "internet_culture",
    "defi",
    "voyage",
    "tech_ia",
    "gaming",
    "wtf_tests",
    "drama_reaction",
    "caritatif",
    "lifestyle",
    "tutoriel_evergreen",
    "enquete",
    "village",
]);
/** Catégories d'événements — enum fermé. Le moteur module les poids PAR CATÉGORIE
 *  (exécution §4.5, alignement §4.7) : voir SPEC-MOTEUR. */
export const EventCategory = z.enum([
    "viralite",
    "good_buzz",
    "bad_buzz",
    "drama",
    "plateforme",
    "materiel",
    "communaute",
    "business",
    "perso",
    "bibliotheque",
    "alignement",
    "opportunite",
]);
/** Moment du tirage (PLAN §4.9 : à la publication et au sommeil, jamais « par mois »). */
export const TriggerId = z.enum(["publication", "sommeil", "les_deux"]);
/**
 * ⟦A-4 décision 2 · A-27⟧ Le genre de l'avatar, **vu du contenu**. Mêmes deux valeurs que
 * `GenreAvatar` (`engine/types.ts`) — la frontière de nommage est volontaire : `content/` ne
 * dépend pas du moteur, c'est le moteur qui lit le contenu.
 *
 * ⚠️ **Il n'y a pas de troisième valeur.** Une run **sans genre** (`genre: null` côté moteur)
 * est l'ÉTAT NEUTRE d'⟦A-10⟧ : elle ne satisfait `genre_requis` d'aucun côté, et aucun
 * `poids_genre` ne s'y applique. C'est ce qui garde les 12 ancres de calibrage intactes.
 */
export const GenreId = z.enum(["f", "h"]);
/**
 * ⟦A-27⟧ Les deux **flags moteur dérivés** du genre (SPEC-CONTENU §5.7). Le contenu ne peut que
 * les LIRE ; c'est le moteur qui répond `state.genre === "f"` / `=== "h"`, exactement comme il
 * répond déjà `monetise`.
 *
 * **Pourquoi ils existent alors que `genre_requis` dit déjà la même chose** : `genre_requis` est
 * l'expression normative, et le moteur ne la lit **pas encore** (`conditionsPassent`,
 * `shared/src/engine/events.ts`). Tant qu'elle n'est pas branchée, ces flags sont le **verrou
 * qui rend la mécanique inerte** : `flagPose(state, "genre_f")` vaut `false` pour tout le monde,
 * donc les événements genrés ne se tirent jamais et l'état neutre d'⟦A-10⟧ est intact au sens
 * fort (les chiffres de `simulate.ts --check` ne bougent pas d'un poil).
 *
 * L'invariant `GENRE_FLAG` ci-dessous **impose** de porter les deux : personne ne peut ajouter un
 * événement genré qui contournerait le verrou par distraction.
 */
export const GENRE_FLAG = { f: "genre_f", h: "genre_h" };
// ─────────────────────────────────────────────────────────────────────────────
// §2.2 Assists de mini-jeux — enum fermé des 19 paramètres (17 de §2.2 + 2 phases)
// ─────────────────────────────────────────────────────────────────────────────
export const AssistParam = z.enum([
    "beats_preplaces",
    "hook_toujours_premier",
    "erreurs_pardonnees",
    "chrono_bonus_pct",
    "retakes_gratuites",
    "fenetres_pct",
    "premiere_fenetre_reussie",
    "fenetre_rythmique_pct",
    "piste_beat_visible",
    "tempo_percu_pct",
    "zone_accroche_pct",
    "curseur_ralenti_pct",
    "relances_gratuites",
    "heatmap_revelee_pct",
    "clips_bonus",
    "clips_cout_energie_zero",
    "zones_clignotent",
    // ⟦A-56⟧ Le 18ᵉ, créé comme le seul qui n'aide pas : il **ajoutait une phase** (le plan lumière).
    // ⟦C53 · A-170 §2⟧ « La prise parfaite » n'a plus de cadre à éclairer : le nom reste (l'enum est
    // fermé, et `skills.json` le référence), le sens change. C'est un **avantage** : un plateau bien
    // éclairé, la zone verte plus large. Il n'ajoute plus aucun geste.
    "phase_lumieres",
    // ⟦A-59⟧ Le 19ᵉ, deuxième de sa famille : la phase de repérage du Montage.
    "phase_reperage",
    // ⟦A-69⟧ Le 20ᵉ : la phase de métrage de la Promotion — on **répartit** un budget de
    // secondes entre les clips avant de les poser. Somme nulle : allonger l'un raccourcit
    // l'autre.
    "phase_metrage",
    // ⟦A-69⟧ Le 21ᵉ : la phase de promesse de la Miniature — on **annonce** une fourchette sur
    // le cadran, elle devient le sweet spot, et il faut ensuite tenir parole.
    "phase_promesse",
]);
export const Assist = z
    .object({
    minigame: MinigameId,
    param: AssistParam,
    value: z.number(),
})
    .strict();
/** Table de compatibilité minigame → params (normative, test T10). */
export const ASSIST_COMPAT = {
    fil_rouge: [
        "beats_preplaces",
        "hook_toujours_premier",
        "erreurs_pardonnees",
        "chrono_bonus_pct",
    ],
    bonne_prise: [
        "retakes_gratuites",
        "fenetres_pct",
        "premiere_fenetre_reussie",
        "phase_lumieres",
    ],
    couper_rythme: [
        "fenetre_rythmique_pct",
        "piste_beat_visible",
        "tempo_percu_pct",
        "phase_reperage",
    ],
    putaclic_o_metre: [
        "zone_accroche_pct",
        "curseur_ralenti_pct",
        "relances_gratuites",
        "phase_promesse",
    ],
    la_decoupe: [
        "heatmap_revelee_pct",
        "clips_bonus",
        "clips_cout_energie_zero",
        "zones_clignotent",
        "phase_metrage",
    ],
};
/**
 * Params booléens : value doit valoir exactement 1.
 *
 * ⟦A-59⟧ **Les deux params de phase y entrent** — `phase_lumieres` y manquait depuis ⟦A-56⟧
 * (omission sans conséquence : le registre ne fait qu'**ajouter** une contrainte au test T10,
 * il n'en retire jamais). Un interrupteur de phase est le plus booléen des params qui soient :
 * `actif()` ne lit qu'un seuil à 1, et « une demi-phase » ne veut rien dire. Les inscrire, c'est
 * empêcher qu'un futur `value: 2` passe le contrôle en promettant deux fois plus de rien.
 */
export const ASSIST_BOOL_PARAMS = [
    "hook_toujours_premier",
    "premiere_fenetre_reussie",
    "piste_beat_visible",
    "clips_cout_energie_zero",
    "zones_clignotent",
    "phase_lumieres",
    "phase_reperage",
    "phase_metrage",
    "phase_promesse",
];
/**
 * ⟦A-60⟧ **Les params qui ajoutent un GESTE — et que seule une compétence peut porter.**
 *
 * ⟦A-55⟧ pose la seule exclusivité du système, et elle va dans un seul sens : *« seule une
 * compétence peut ajouter un geste, une phase, une mécanique »*, parce qu'**un objet se
 * possède, il ne s'apprend pas** (post-scriptum d'A-55). Tous les autres params sont des
 * **statistiques de mini-jeu** — largeur de fenêtre, erreur pardonnée, repère révélé — et
 * ceux-là, une compétence ET un objet peuvent les donner : ce qui les sépare est la durée
 * ⟦A-50 ter⟧.
 *
 * Ce registre est ce qui rend la règle **vérifiable au boot** au lieu d'être une intention
 * dans un avenant : `BoutiqueObjet` refuse ces params, `Skill` les accepte.
 *
 * 📌 **À tenir à jour** : toute session qui ajoute une phase (le patron d'⟦A-56⟧, poursuivi
 * par ⟦A-59⟧) inscrit son param **ici** en même temps que dans `ASSIST_BOOL_PARAMS`. Un
 * oubli ne casse rien tout de suite — il ouvre seulement la porte à un objet qui vendrait
 * une phase, c'est-à-dire à la compétence mal rangée qu'A-55 dit de refuser.
 */
export const ASSIST_PARAMS_GESTE = [
    // ⟦C53 · A-170 §2⟧ `phase_lumieres` en est sorti : le plan lumière (placer des lampes qui
    // flottent) est parti avec le cadre et la grille des tiers de « La bonne prise ». Le param
    // reste, booléen, et ne fait plus qu'élargir la zone verte de « La prise parfaite » : une
    // statistique de mini-jeu, pas un geste. `skills.json` (*Le Setup*) est le seul à le porter.
    "phase_reperage",
    "phase_metrage",
    "phase_promesse",
];
/**
 * Les 5 étapes du pipeline d'Atelier — mêmes valeurs que `AtelierStage` du moteur.
 *
 * ⟦A-61⟧ Déclaré **ici**, avant l'enum des effets, et non plus dans la section des
 * freelances : `freelance_direction` en a besoin, et un enum ne se lit pas avant d'être écrit.
 * Aucune valeur n'a changé.
 */
export const EtapeAtelierId = z.enum([
    "ecriture",
    "tournage",
    "montage",
    "miniature",
    "promotion",
]);
/**
 * ⟦A-61⟧ Cible d'une compétence de **direction** : une étape, ou toutes.
 *
 * `toutes` n'est pas un raccourci d'écriture — c'est la portée d'un nœud qui n'appartient à
 * aucune branche d'Atelier (« Le Studio » est du Business) et qui améliore **l'équipe**, pas
 * un métier. Le moteur la déplie sur les cinq étapes.
 */
export const CibleDirection = z.enum([
    "ecriture",
    "tournage",
    "montage",
    "miniature",
    "promotion",
    "toutes",
]);
// ─────────────────────────────────────────────────────────────────────────────
// §2.3 L'ENUM FERMÉ des effets — les 18 SEULS types que le moteur implémente
// ─────────────────────────────────────────────────────────────────────────────
export const Effect = z.discriminatedUnion("type", [
    z.object({ type: z.literal("subs_pct"), value: z.number().min(-15).max(15) }).strict(),
    z.object({ type: z.literal("subs_flat"), value: z.number().int() }).strict(),
    z.object({ type: z.literal("gauge_delta"), gauge: GaugeId, value: z.number() }).strict(),
    z
        .object({
        type: z.literal("flag_add"),
        flag: Id,
        expires_h: z.number().positive().optional(),
    })
        .strict(),
    z.object({ type: z.literal("flag_remove"), flag: Id }).strict(),
    z.object({ type: z.literal("unlock"), capability: CapabilityId }).strict(),
    z
        .object({ type: z.literal("potential_add"), factor: FactorId, value: z.number() })
        .strict(),
    z
        .object({
        type: z.literal("multiplier"),
        target: MultiplierTarget,
        value: z.number().positive(),
        duree_h: z.number().positive().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal("assist"),
        minigame: MinigameId,
        param: AssistParam,
        value: z.number(),
    })
        .strict(),
    z
        .object({
        type: z.literal("event_weight_mult_event"),
        event_id: Id,
        value: z.number().min(0),
    })
        .strict(),
    z
        .object({
        type: z.literal("event_weight_mult_categorie"),
        categorie: EventCategory,
        value: z.number().min(0),
    })
        .strict(),
    z
        .object({
        type: z.literal("action_cost_mult"),
        action: ActionId,
        ressource: ResourceId,
        value: z.number().positive(),
        /**
         * ⟦A-9⟧ Durée en heures éveillées — **exactement comme `multiplier` la porte déjà**.
         * Absent = permanent, c'est-à-dire le comportement d'avant : l'état neutre.
         *
         * C'est ce champ qui sépare **une boisson d'un meuble**. Sans lui, un consommable de
         * boutique est ou bien un one-shot de jauge (`gauge_delta` le fait déjà), ou bien un
         * bonus définitif — donc pas un consommable.
         */
        duree_h: z.number().positive().optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal("money_per_video"),
        format: z.enum(["short", "longue", "toutes"]),
        value: z.number(),
    })
        .strict(),
    z.object({ type: z.literal("money_per_1k_subs_weekly"), value: z.number() }).strict(),
    z
        .object({
        /**
         * ⟦A-9⟧ **Ce que la nuit rend en plus** — s'ajoute au `+80 ⚡ / +3 SM` du pas 1 de
         * SPEC-MOTEUR §2.4. C'est le point d'application qui manquait au **matériel de vie**
         * (lit, rideaux, casque, insonorisation) : aucun des 18 types existants ne touchait la
         * nuit. `gauge_delta` est un one-shot ; `action_cost_mult` ne s'applique qu'aux COÛTS,
         * et le sommeil est un GAIN que `actionCost` refuse volontairement de multiplier.
         *
         * **Neutre à 0** : sans objet acheté, la nuit vaut exactement ce qu'elle valait, donc
         * aucune ancre de calibrage ne bouge (⟦A-10⟧).
         */
        type: z.literal("sleep_recovery_add"),
        gauge: z.enum(["energie", "sante_mentale"]),
        value: z.number(),
    })
        .strict(),
    z
        .object({
        /**
         * ⟦A-61 · A-50 §4⟧ **La direction** — ce que votre niveau apporte au travail que vous
         * ne faites plus vous-même.
         *
         * Le défaut qu'il répare est nommé par A-50 §4 : *« aujourd'hui, déléguer rend le build
         * obsolète — la cote du freelance remplace le score du joueur, donc les PC investis
         * cessent de payer sur cette étape au moment de la première embauche. »* Le jeu punissait
         * d'avoir progressé au moment où il proposait de souffler.
         *
         * `value` est la **part du chemin** parcourue entre ce que le freelance rend seul et ce
         * que la chaîne saurait exploiter (`plafondCompetence`, ⟦A-9/A-33⟧) — donc un nombre de
         * `]0;1]`, cumulé **additivement** entre nœuds. Deux propriétés en découlent, et ce sont
         * elles qui rendent l'effet sûr :
         *
         *  1. **Il ne fabrique jamais de points à partir de rien** : sans arbre sur l'étape, le
         *     plafond est bas, le chemin est court, la direction ne donne presque rien. C'est
         *     exactement le sens voulu — *le freelance travaille mieux parce que vous savez
         *     mieux diriger.*
         *  2. **Il ne franchit pas `COTE_FREELANCE_PLAFOND`** (82, ⟦A-45 règle 1⟧) : le moteur
         *     borne le résultat. Un joueur qui joue bien reste meilleur que la meilleure équipe.
         *
         * ⚠️ **C'est une STATISTIQUE, pas un geste** — une compétence a le droit des deux
         * ⟦A-55⟧, et sur une étape déléguée c'est la seule forme possible, le joueur n'étant
         * pas là (A-50 §4, exception assumée). **Neutre à 0** : aucun nœud porteur ⇒ la
         * livraison est celle d'avant, à l'unité près.
         */
        type: z.literal("freelance_direction"),
        etape: CibleDirection,
        value: z.number().positive().max(1),
    })
        .strict(),
    z.object({ type: z.literal("unsub_mult"), value: z.number().positive() }).strict(),
    z.object({ type: z.literal("strike_delta"), value: z.number().int() }).strict(),
    z.object({ type: z.literal("pc_add"), value: z.number().int().positive() }).strict(),
    z
        .object({
        type: z.literal("event_schedule"),
        event_id: Id,
        delai_h: z.number().positive(),
    })
        .strict(),
    z
        .object({
        /**
         * ⟦A-67 · A-4 décision 6 · A33⟧ **La prise du contenu sur `channel_image_offset`** — le
         * 21ᵉ type, et le premier depuis ⟦A-61⟧.
         *
         * **Le trou qu'il bouche est nommé trois fois dans les avenants.** ⟦A33⟧ a créé l'offset
         * précisément *« pour que les comportements hors publication (pause abusive, dons,
         * excuses) aient une prise »* — et n'a donné cette prise qu'au moteur : `pause.ts` puis
         * `fil.ts` l'écrivent, **aucun type d'effet ne le pouvait**. Conséquences relevées :
         *  - ⟦A-27⟧ : les « image +N » de cinq événements genrés (E2, E3, E5, E8, E10) ont été
         *    rendus en `gauge_delta reputation`, *« la jauge de standing public que le contenu
         *    utilise déjà »* — un pis-aller signalé comme tel ;
         *  - ⟦A-63⟧ : les dons caritatifs sont restés en intention, faute de ce type.
         *
         * 🔑 **`plafond` est ce qui rend l'effet SÛR, et il vient mot pour mot d'⟦A-4 décision
         * 6⟧** : *« La charité n'est JAMAIS un levier d'optimisation […] elle ne peut que
         * réparer une image abîmée. »* Présent, il borne l'offset **résultant** — un don peut
         * effacer une cicatrice, il ne peut jamais fabriquer une auréole. La règle devient
         * structurelle au lieu d'être une consigne d'écriture qu'un futur événement oublierait.
         *
         * Absent, l'effet est un `+N` / `−N` ordinaire sur l'offset : c'est la forme dont les
         * cinq événements d'⟦A-27⟧ avaient besoin.
         *
         * ⚠️ **Ce n'est PAS un `gauge_delta`, et `image` n'entre pas dans `GaugeId`.** L'image de
         * chaîne est **dérivée** (§7.1 : moyenne des 10 derniers alignements + offset) : lui
         * donner une entrée de jauge laisserait croire qu'on peut l'écrire directement, alors
         * que la prochaine publication la recalculerait. On écrit la cicatrice, pas le visage.
         */
        type: z.literal("image_offset"),
        value: z.number(),
        plafond: z.number().min(-100).max(100).optional(),
    })
        .strict(),
    z
        .object({
        /**
         * ⟦A-84 · A-4 arbitrage D · SPEC-MOTEUR §7.2bis⟧ **La prise du contenu sur l'axe
         * relationnel d'un PNJ** — le 22ᵉ type, et celui que `rivaux.ts` annonçait mot pour mot
         * (« il faudra un type `rival_relation_delta` — un vrai avenant, pas un ajout discret »).
         *
         * §7.2bis : *« la relation se CONSTRUIT selon les réponses du joueur aux événements qui
         * les impliquent »*. Depuis M3.16, `RivalEtat.relation` existait, `bandeRelation` la
         * lisait, `appliquerRelationRival` savait l'écrire — et **aucun contenu ne pouvait
         * l'appeler**. La bande rival / connaissance / pote était donc calculable, affichable, et
         * immobile à 0 pour toujours : la troisième voie (« ignorer ») était la seule jouable.
         *
         * `value` : ±N sur l'axe [−100 ; +100] (bandes à ±40, `SEUIL_RIVAL` / `SEUIL_POTE`).
         * `cible` : QUEL PNJ — le contenu ne connaît pas les ids de la run (2-3 tirés parmi 8) :
         *  - `plus_proche` (défaut) : celui dont les abonnés sont les plus proches des tiens —
         *    « un créateur de ta taille » ;
         *  - `devant` : le plus proche de ceux qui te dépassent — « un grand te cite » ;
         *  - `derriere` : le plus proche de ceux que tu dépasses ;
         *  - `tous` : les 2-3 d'un coup (le milieu entier, quand une réponse est publique).
         * `devant` / `derriere` retombent sur `plus_proche` quand personne ne remplit le rôle.
         *
         * ⚠️ **Aucun effet mécanique sur les jauges** : les PNJ sont un miroir (`rivaux.ts`,
         * en-tête), et la relation ne fait que nommer ce que le joueur a construit. C'est ce qui
         * rend l'effet NEUTRE pour l'état neutre ⟦A-10⟧ par construction — `simulate.ts` peut
         * prendre un choix qui le porte sans qu'un seul chiffre du rapport bouge.
         */
        type: z.literal("rival_relation_delta"),
        value: z.number().int().min(-100).max(100),
        cible: z.enum(["plus_proche", "devant", "derriere", "tous"]).optional(),
    })
        .strict(),
]);
/** Les 21 types de l'enum fermé, dans l'ordre du §2.3 (+ `sleep_recovery_add` ⟦A-9⟧,
 *  + `freelance_direction` ⟦A-61⟧, + `image_offset` ⟦A-67⟧) — pratique pour les tests (T17)
 *  et pour l'exhaustivité du moteur d'effets (M1.8). */
export const EFFECT_TYPES = [
    "subs_pct",
    "subs_flat",
    "gauge_delta",
    "flag_add",
    "flag_remove",
    "unlock",
    "potential_add",
    "multiplier",
    "assist",
    "event_weight_mult_event",
    "event_weight_mult_categorie",
    "action_cost_mult",
    "money_per_video",
    "money_per_1k_subs_weekly",
    "sleep_recovery_add",
    "freelance_direction",
    "unsub_mult",
    "strike_delta",
    "pc_add",
    "event_schedule",
    "image_offset",
    "rival_relation_delta",
];
// ─────────────────────────────────────────────────────────────────────────────
// §3.1 meta.json
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// ⟦C60 · A-177⟧ Le grand ménage : un interrupteur par système, rien de supprimé
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Les systèmes que le grand ménage ⟦C60 · A-177⟧ peut COUPER. Liste fermée : c'est le
 * vocabulaire de `meta.systemes` et de `meta.evenements_par_systeme`.
 *
 *  - `charges` : le loyer et les charges par jour, et toute dépense subie (la perte d'argent
 *    d'un événement). L'argent ne sert plus qu'à payer le Marché et l'Équipe ;
 *  - `faillite` : la descente vers l'argent négatif, ses alertes, la fin par faillite, le
 *    sponsor « dernière chance » ;
 *  - `boutique` : les objets et les consommables ;
 *  - `logement` : chez les parents, le studio (`retour_parents`, `reprendre_studio`) ;
 *  - `jalons` : les vidéos jalons à tenter par palier (les paliers, eux, restent) ;
 *  - `bibliotheque` : la gestion de la bibliothèque (ménage, série, remaster) et le malus des
 *    vidéos nulles visibles. La rente des anciennes vidéos continue de tourner seule ;
 *  - `strikes` : les strikes et le bannissement ;
 *  - `bad_buzz` : le bad buzz comme SANCTION (l'arc de la chute, les excuses, la spirale de
 *    réputation). Les événements de bad buzz gardent leurs choix et leurs effets d'abonnés,
 *    d'énergie et de santé mentale ;
 *  - `alignement` : l'alignement des sujets, l'image de chaîne (lumière / chaos) et ses effets ;
 *  - `reputation` ⟦C60 bis⟧ : la réputation est VISIBLE (HUD, télécommande, leçons, infobulles,
 *    récap, journal). Éteinte, la jauge sort de l'écran sans disparaître du moteur ;
 *  - `production_value` ⟦C60 bis⟧ : la production value (jusqu'à 500 ₵ par vidéo dès 100 000
 *    abonnés) existe. Éteinte, l'action qui en porte une est refusée et l'écran n'en parle plus.
 */
export const SYSTEMES_COUPABLES = [
    "charges",
    "faillite",
    "boutique",
    "logement",
    "jalons",
    "bibliotheque",
    "strikes",
    "bad_buzz",
    "alignement",
    "reputation",
    "production_value",
];
export const SystemeCoupable = z.enum(SYSTEMES_COUPABLES);
/**
 * ⟦C60 · A-177⟧ **Les interrupteurs du grand ménage.** Chaque booléen vaut « le système est
 * ACTIF » : le défaut est `true` partout, c'est-à-dire LE JEU D'AVANT, au caractère près (l'état
 * neutre ⟦A-10⟧ : un dossier de contenu qui ne mentionne pas `systemes` rejoue le jeu d'avant le
 * ménage, et `simulate.ts --menage tout` le prouve au `md5`). C'est `content/meta.json` qui
 * COUPE, système par système : revenir au jeu d'avant est **une ligne par système**.
 *
 * `capital_grand_saut` : les Clicks versés au palier des 1 000 abonnés (défaut `600`, la valeur
 * d'avant). À `0`, le palier de 1 000 reste un palier (les PC, la plaque, l'ouverture de l'argent)
 * et ne verse plus rien.
 *
 * ⟦C60 bis · A-177⟧ **Les Clicks ne paient plus que le Marché et l'Équipe.** Quatre réglages, chacun
 * à la valeur d'AVANT par défaut (l'état neutre ⟦A-10⟧), que `content/meta.json` fait descendre :
 *  - `respec_clicks` : prix de base du respec, doublé à chaque respec (défaut `250`, `0` = gratuit) ;
 *  - `therapie_clicks` : Clicks d'une séance de thérapie (défaut `150`, `0` = plus de Clicks) ;
 *  - `therapie_energie` : énergie d'une séance (défaut `4` ; le contenu livré la monte, puisque la
 *    thérapie se paie désormais en temps et en énergie) ;
 *  - `reprise_clicks` : part des Clicks du palier que la reprise après un burn-out garde (défaut
 *    `0.5`, `1` = la chute ne retire plus rien) ;
 *  - `coussin_jours` : le coussin du producteur (et le seuil de tension du manager), en jours de lignes
 *    fixes : la caisse que le producteur ne dépense jamais (défaut `14`, `SEUIL_TENSION_J`).
 */
export const SystemesFile = z
    .object({
    charges: z.boolean().default(true),
    faillite: z.boolean().default(true),
    boutique: z.boolean().default(true),
    logement: z.boolean().default(true),
    jalons: z.boolean().default(true),
    bibliotheque: z.boolean().default(true),
    strikes: z.boolean().default(true),
    bad_buzz: z.boolean().default(true),
    alignement: z.boolean().default(true),
    reputation: z.boolean().default(true),
    production_value: z.boolean().default(true),
    capital_grand_saut: z.number().int().min(0).max(100_000).default(600),
    respec_clicks: z.number().int().min(0).max(100_000).default(250),
    therapie_clicks: z.number().int().min(0).max(100_000).default(150),
    therapie_energie: z.number().int().min(0).max(100).default(4),
    reprise_clicks: z.number().min(0).max(1).default(0.5),
    coussin_jours: z.number().int().min(0).max(60).default(14),
})
    .strict();
/**
 * ⟦C62 · lot événements · A-179⟧ **La fenêtre d'événement, lisible par un débutant.** Trois réglages d'ÉCRAN,
 * jamais de règle : le moteur n'en lit aucun. Absent (ou un contenu antérieur) ⇒ l'écran d'AVANT, au
 * caractère près : l'état neutre ⟦A-10⟧, et dépiler est une ligne.
 *
 *  - `masquer_verrouilles_avant_arbre` : tant que l'Arbre n'est pas ouvert pour le joueur, les choix
 *    verrouillés (🔒 « nécessite : … ») et l'étiquette « parade » n'existent pas à l'écran : ils renvoient à
 *    un système que le joueur n'a pas encore. Défaut `false` (ils s'affichent dès le premier événement) ;
 *  - `consequences_visibles` : chaque choix dit ce qu'il fait EN CHIFFRES ET EN ICÔNES (`−3 ❤`, `+20 👥`,
 *    `−1 idée`), lu dans les `effets` du contenu par le moteur lui-même (aucun chiffre recopié, aucune règle
 *    réécrite). Défaut `false` (le bouton ne montre que son libellé, comme ⟦SPEC-UI §3.6⟧ le demandait) ;
 *  - `libelles_categories` : le MOT DE JOUEUR de chaque catégorie, au lieu de l'identifiant (`bad_buzz` se
 *    lisait « BAD BUZZ »). Une catégorie absente garde son identifiant, comme avant.
 */
export const EvenementsEcranFile = z
    .object({
    masquer_verrouilles_avant_arbre: z.boolean().default(false),
    consequences_visibles: z.boolean().default(false),
    libelles_categories: z.record(EventCategory, z.string().min(1)).default({}),
})
    .strict();
export const MetaFile = z
    .object({
    content_version: z.string().regex(/^\d+\.\d+\.\d+$/),
    /**
     * ⟦SPEC-FIL §10 / A-57⟧ **L'interrupteur général du Fil et de la Hype.**
     *
     * À `false` : aucune arrivée de commentaire, aucune décroissance de Hype, aucun bonus de
     * Portée — le jeu retombe **au caractère près** sur son comportement d'avant le Fil. C'est
     * le corollaire non négociable d'⟦A-15⟧ : empiler n'a de sens que si dépiler est **une
     * ligne**.
     *
     * ⚠️ **Le défaut est `false`, là où SPEC-FIL §10 écrivait `true`** — amendement ⟦A-57⟧,
     * assumé et daté. Le socle moteur (M3.20) est livré **éteint** parce que les cinq réponses
     * (M3.21), le contenu complet (M3.22) et l'écran (M3.23) n'existent pas encore : allumer
     * ferait arriver des commentaires que le joueur ne pourrait ni voir ni traiter. Le jour où
     * M3.23 est livré, ce défaut redevient `true` — c'est un caractère à changer, et c'était
     * tout l'objet de l'interrupteur.
     */
    fil_actif: z.boolean().default(false),
    /**
     * ⟦SPEC-AXE-PRODUIT §7 / A-62⟧ **L'interrupteur général de l'axe Produit ↔ Authentique.**
     *
     * À `false` : `produit_authentique` reste `null` pour toute la run, aucun journal de flux
     * n'est ouvert, aucune dérive n'est détectée, et un événement portant `produit_min` /
     * `produit_max` reste inéligible. Le jeu retombe **au caractère près** sur son
     * comportement d'avant l'axe — corollaire d'⟦A-15⟧ : dépiler doit être **une ligne**.
     *
     * ⚠️ **Le défaut est `false`, là où SPEC-AXE-PRODUIT §9.1 ✎ 12 écrivait `true`** —
     * amendement assumé et daté, même motif et même précédent que le Fil ⟦A-57⟧ : le socle
     * moteur est livré avant les ≥ 13 événements de §6.4, qui vivent dans `content/events.json`.
     * Allumer maintenant montrerait au joueur un libellé de bande que rien ne vient jamais
     * commenter. Le jour où ce contenu existe, c'est **un caractère à changer**.
     */
    axe_produit_actif: z.boolean().default(false),
    /**
     * ⟦SPEC-PRODUCTEUR §9 / A-66⟧ **L'interrupteur général de l'escalier de délégation.**
     *
     * À `false` : les nœuds gardés par un seuil d'abonnés (`abonnes_min`) sont **filtrés au
     * chargement, des deux côtés** (`content/load.ts` pour le serveur, `client/src/game/content.ts`
     * pour le bac à sable — la parité ⟦A-32⟧ a un test), et `laisser_tourner` / `regler_equipe`
     * sont refusées en `mecanique_verrouillee`. `content/skills.json` se relit donc sans le
     * producteur, et le jeu retombe **au caractère près** sur son comportement d'avant
     * l'avenant. ⟦A-85, 22/08⟧ **ALLUMÉ** dans `content/meta.json` : l'arbre servi est FERMÉ à
     * **41 nœuds / 203 PC** (`load.test.ts`, `determinism.test.ts`, T9, `skills.test.ts`).
     *
     * ⚠️ **Le défaut est `false`, là où SPEC-PRODUCTEUR §9 ✎ 14 écrivait `true`** — amendement
     * ⟦A-66⟧, même motif et même précédent que le Fil ⟦A-57⟧ et l'axe Produit ⟦A-62⟧ : le socle
     * moteur du **producteur** est livré avant le **manager** (§5), avant l'écran de
     * récapitulatif animé (§5.5) et surtout avant que la question du §6.4 soit tranchée — le
     * marché des freelances n'ouvre qu'à **J784 / 329 621 abonnés** en médiane mesurée, donc
     * un producteur allumé aujourd'hui serait acheté après le million. Allumer, c'est **un
     * caractère**, et c'était tout l'objet de l'interrupteur ⟦A-15⟧.
     */
    equipe_active: z.boolean().default(false),
    /**
     * ⟦A-77 §4 · M3.29⟧ **L'interrupteur PROPRE de l'étage 3 (le manager).**
     *
     * L'avenant l'exige mot pour mot : si le critère de l'étage 2 passe au vert et que le
     * manager n'est pas prêt, « on sépare les interrupteurs plutôt que d'allumer un étage
     * inachevé ». À `false` : le nœud `le_manager` est filtré au chargement des deux côtés,
     * et le moteur ne lit jamais `consignes.rythme` — cadence `raisonnable`, repos `jamais`,
     * aucun nœud visé, exactement le monde d'avant M3.29.
     *
     * ⚠️ Il est ET avec `equipe_active` : un manager sans producteur tiendrait le calendrier
     * d'une chaîne qui ne publie pas (§2.1 — l'escalier est littéral). Le défaut est `false`,
     * même précédent que le Fil ⟦A-57⟧, l'axe ⟦A-62⟧, l'escalier ⟦A-66⟧ et le guide ⟦A-71⟧.
     */
    manager_actif: z.boolean().default(false),
    /**
     * ⟦A-44 · A-71⟧ **Le guide MeTube** — l'interrupteur du nœud *Le guide MeTube* et de
     * l'écran du journal.
     *
     * À `false` : le nœud est filtré au chargement **des deux côtés** (même filtre, même
     * fonction que l'escalier), l'arbre se relit sans le guide, et l'écran du journal est
     * injoignable. Le jeu retombe au caractère près sur son comportement d'avant — c'est
     * l'état neutre ⟦A-10⟧ obtenu **par construction**, pas par mesure.
     *
     * ⚠️ **Le défaut est `false`** : un dossier de contenu qui ne mentionne pas le guide est,
     * par définition, antérieur au guide. Même précédent que le Fil ⟦A-57⟧, l'axe Produit
     * ⟦A-62⟧ et l'escalier ⟦A-66⟧.
     *
     * 🔔 **Ce qu'allumer a coûté, et c'est Mathieu qui l'a tranché ⟦A-85, 22/08⟧** : avec le
     * producteur ⟦A-77⟧, le nœud fait passer `business` de 6 à 8, l'arbre de 39 à **41** et son
     * prix de 195 à **203 PC**. Les invariants écrits comme fermés (`determinism.test.ts`,
     * `skills.test.ts` ×3, `coherence.test.ts` T9, `load.test.ts`) ont bougé avec lui en citant
     * l'avenant ; `content/meta.json` porte `guide_actif: true` depuis la version 1.7.0.
     */
    guide_actif: z.boolean().default(false),
    /**
     * ⟦C62 · lot événements · A-179⟧ **Les réglages d'écran de la fenêtre d'événement** (voir
     * `EvenementsEcranFile`). Facultatif : absent ⇒ l'écran d'avant, au caractère près.
     */
    evenements_ecran: EvenementsEcranFile.optional(),
    /**
     * ⟦C46 · A-168 §1⟧ **Le rythme de la partie** : le facteur global de sa longueur.
     *
     * Matt (30/09/2026) : *« la simulation est trop longue »* : la partie entière, trop de vidéos
     * et de jours pour arriver au million. Ce nombre fait compter une vidéo RÉUSSIE pour `k`
     * vidéos : il multiplie ses vues (donc ses abonnés, ses Clicks et son XP, d'un seul geste),
     * voir `rythmeDeLaVideo` dans `engine/formula.ts`. Deux garde-fous, dans le moteur : il ne
     * joue qu'à partir de 1 000 abonnés (l'école du jeu ⟦A-1⟧ est intacte), et seulement pour
     * une exécution correcte (55 → 75) : le joueur faible n'en tire rien, ⟦A-22⟧ est tenu.
     *
     * ⚠️ **Le défaut est `1` : l'état neutre ⟦A-10⟧.** Un dossier de contenu qui ne mentionne
     * pas le rythme est antérieur à ce facteur et rejoue le jeu d'avant au caractère près.
     * `content/meta.json` porte la valeur du jeu servi (`6` : environ trois fois moins de
     * vidéos pour un bon joueur) ; revenir au jeu d'avant est **une ligne**, et
     * `simulate.ts --rythme 1` le prouve au `md5`.
     */
    rythme_croissance: z.number().positive().max(10).default(1),
    /**
     * ⟦C62 · lot moteur · A-179⟧ **Les événements arrivent quand le joueur a vu les gestes** : le nombre de
     * publications à avoir FAITES avant que le moindre événement puisse se tirer.
     *
     * L'audit « regard neuf » du 21/10 : trois événements en trois vidéos, avec des choix verrouillés que le
     * joueur ne pouvait pas lire. Tant que `counters.videos_published` est en dessous, AUCUN événement ne se
     * pose, y compris la viralité, un rendez-vous programmé ou une passation : ils attendent, ils ne sont
     * pas perdus (`tirerEvenement`, `engine/events.ts`).
     *
     * ⚠️ **Le défaut est `0` : l'état neutre ⟦A-10⟧.** Un dossier de contenu qui ne mentionne pas ce réglage
     * rejoue le jeu d'avant, où le premier tirage suit la toute première vidéo, au caractère près.
     * `content/meta.json` porte `3`, la publication où s'ouvre la lecture des commentaires. Ce n'est PAS
     * un interrupteur du ménage (`systemes`) : le bras témoin `avantLeMenage` ne le touche pas, comme le
     * rythme de la partie.
     */
    evenements_apres_publications: z.number().int().min(0).max(20).default(0),
    /**
     * ⟦C62 bis · A-179⟧ **Le repos de l'Équipe quand le joueur n'a rien réglé** (la consigne `rythme.repos` du manager).
     *
     * Matt (22/10/2026) : *« Vas-y, corrige le 1 et le 2. »* Le repos valait « jamais » : la santé mentale, c'était au
     * joueur, et « Laisser tourner » s'arrêtait sur « à bout » (`sante_mentale`) dès qu'elle passait sous le plancher
     * de la cadence. Ce réglage dit ce que fait l'équipe tant que le joueur ne choisit pas : `jamais` (elle rend la
     * main), `therapie` (le manager va voir quelqu'un, du temps et de l'énergie, et la machine continue) ou `tout`
     * (il annonce la pause UNE fois, puis dort : le sommeil l'engage ⟦C62 ter⟧). Il ne vaut que pour une consigne JAMAIS
     * réglée : une consigne écrite par le joueur (même « jamais ») gagne toujours, et les seuils sont ceux de la cadence
     * (`CADENCES`, inchangés).
     *
     * ⚠️ **Le défaut est `jamais` : l'état neutre ⟦A-10⟧.** Un dossier de contenu qui ne mentionne pas ce réglage rejoue
     * le jeu d'avant au caractère près. `content/meta.json` porte la valeur du jeu servi.
     */
    repos_par_defaut: z.enum(["jamais", "therapie", "tout"]).default("jamais"),
    /**
     * ⟦C62 bis · A-179⟧ **Aucun événement pendant l'action qui grave la victoire.**
     *
     * Matt (22/10/2026) : l'événement tiré la nuit même du million s'affichait par-dessus l'écran « LE MILLION » : le
     * moteur interdit toute autre action tant qu'un événement attend, donc le joueur devait le trancher avant de voir
     * sa victoire. À `true`, l'action qui grave la victoire (les abonnés de la fin, ou le pic de l'action, touchent le
     * million et `victory_locked` est encore faux) ne tire RIEN, comme la porte des trois publications
     * (`evenements_apres_publications`) : ni événement, ni viralité, ni rendez-vous programmé, ni passation, aucun
     * tirage consommé. Le tirage reprend à l'action suivante, après « Continuer » vers le 100 % (`tirerEvenement`).
     *
     * ⚠️ **Le défaut est `false` : l'état neutre ⟦A-10⟧.** Un dossier de contenu qui ne mentionne pas ce réglage rejoue le
     * jeu d'avant, où l'événement se tire aussi sur l'action de la victoire, au caractère près. `content/meta.json`
     * porte `true`. Ce n'est PAS un interrupteur du ménage (`systemes`).
     */
    pas_d_evenement_a_la_victoire: z.boolean().default(false),
    /**
     * ⟦C60 · A-177⟧ **Les interrupteurs du grand ménage**, un par système coupable (voir
     * `SystemesFile`). Absent ⇒ tout est actif ⇒ le jeu d'avant, au caractère près.
     */
    systemes: SystemesFile.default({}),
    /**
     * ⟦C60 · A-177⟧ **Les événements qui n'existent que pour un système**, par système. Quand
     * l'interrupteur du système est éteint, ses événements ne sont plus tirés (ni tirés au
     * hasard, ni programmés par un `event_schedule`) : c'est le contenu qui les désactive, pas
     * le moteur qui les connaît. Un événement peut figurer sous plusieurs systèmes (il est alors
     * désactivé dès que l'un d'eux est coupé). Absent ⇒ aucun événement n'est lié à un système.
     *
     * Le fichier `events.json` n'est pas touché : l'événement reste écrit, validé, relisible.
     */
    evenements_par_systeme: z.record(SystemeCoupable, z.array(Id)).default({}),
    /**
     * ⟦C60 · lot b · A-177⟧ **Les leçons de Matt qui n'existent que pour un système**, par système :
     * les ids de `LECONS` (`client/src/game/decouverte.ts`, ex. `image`) et de `content/onboarding.json`
     * (ex. `image_de_chaine`). Le système coupé, ses leçons ne sont plus montrées : le texte reste
     * écrit, rien n'est supprimé. Même patron que `evenements_par_systeme`. Absent ⇒ aucune.
     */
    lecons_par_systeme: z.record(SystemeCoupable, z.array(Id)).default({}),
    /**
     * ⟦C60 · lot b · A-177⟧ **Les dossiers du Guide qui n'existent que pour un système**, par système
     * (ids de `content/guide.json`). Le système coupé, le dossier sort du sommaire : il reste dans le
     * fichier, relisible, et revient en rallumant l'interrupteur. Absent ⇒ aucun.
     */
    dossiers_par_systeme: z.record(SystemeCoupable, z.array(Id)).default({}),
    /**
     * ⟦C62 · lot tableau · A-179⟧ **Le tableau de bord limpide** (friction n° 1 de l'audit du 21/10/2026).
     *
     * Matt : *« Il faut que le jeu soit simple et limpide. »* À `true`, la fenêtre « Ma chaîne » ne garde
     * sous les yeux que ce qui peut tuer ou faire gagner (les abonnés, l'énergie, la santé mentale, les
     * Clicks dès le Grand Saut), chacun avec son NOM écrit à côté de son icône ; tout le reste (fidélité,
     * hype, momentum, niveau, ratio vues par abonné, surcharge d'énergie, rivaux) se replie dans un
     * « Détails » fermé par défaut.
     *
     * ⚠️ **Le défaut est `false` : l'état neutre ⟦A-10⟧.** Un dossier de contenu qui ne mentionne pas
     * ce réglage est antérieur à ce chantier et rejoue le tableau d'avant (toutes les jauges à plat, sans
     * nom écrit) au pixel près. `content/meta.json` le passe à `true` ; revenir au tableau d'avant est
     * **une ligne**. Purement visuel : le moteur ne le lit jamais, aucun barème ne bouge.
     */
    tableau_limpide: z.boolean().default(false),
    /**
     * ⟦C62 · lot tableau · A-179⟧ **La création de partie limpide** (friction n° 10).
     *
     * À `true`, l'étape ③ de la création ne pose plus Mode, Où vit ta partie ni Graine : des défauts
     * silencieux (Normal, la partie locale, la graine au hasard) et un lien « Options » discret qui rouvre
     * ces choix. Défaut `false` : la création d'avant, au caractère près (⟦A-10⟧). Purement visuel.
     */
    creation_limpide: z.boolean().default(false),
    // ⟦C62 · lot arbre · A-179⟧ ─────────────────────────────────────────────────────────────────────
    /**
     * ⟦C62 · lot arbre · A-179⟧ **L'Arbre se dévoile marche par marche.** Matt, 21/10/2026 : *« Il faut que le jeu soit
     * simple et limpide. »* À 100 abonnés le joueur arrive avec 2 points et l'Arbre lui montrait 42 nœuds d'un coup.
     * À `true`, l'écran ne montre que les nœuds acquis et ceux dont TOUS les prérequis sont pris (un nœud par
     * branche au départ) : le suivant se dévoile quand le sien est pris. Un réglage d'AFFICHAGE : aucun prix, aucun
     * prérequis, aucun effet ne change, le moteur ne le lit pas.
     *
     * ⚠️ **Le défaut est `false` : l'état neutre ⟦A-10⟧**, l'Arbre d'avant (42 nœuds visibles d'un coup, grisés
     * tant que leur prérequis manque). `content/meta.json` le passe à `true`.
     */
    arbre_progressif: z.boolean().default(false),
})
    .strict();
// ─────────────────────────────────────────────────────────────────────────────
// §3.2 skills.json
// ─────────────────────────────────────────────────────────────────────────────
/** Table tier → coût PC, normative (PLAN §4.12). Total de l'arbre = 195 PC ✎ (test T9). */
/** ⟦SPEC-PRODUCTEUR §11.2 · M3.29⟧ Le tier 7 existe pour UN nœud : `le_manager` (16 PC). */
export const TIER_COST = { 1: 1, 2: 2, 3: 3, 4: 5, 5: 8, 6: 12, 7: 16 };
export const Skill = z
    .object({
    id: Id,
    branche: BranchId,
    tier: z.number().int().min(1).max(7), // 7 : le_manager seul (SPEC-PRODUCTEUR §11.2)
    nom: z.string().min(1),
    description: z.string().min(1),
    cout_pc: z.number().int().positive(), // DOIT valoir TIER_COST[tier] (test T9)
    cout_euros: z.number().int().min(0).default(0), // > 0 seulement pour matériel/embauche (PLAN §4.12)
    prerequis: z.array(Id).default([]), // ids de nœuds, même branche (test T6)
    /**
     * ⟦SPEC-PRODUCTEUR §2.2 · A-66⟧ **Une porte d'abonnés sur un nœud** — `acheter_skill` est
     * refusée (`skill_locked_subs`) tant que `subscribers < abonnes_min`, et **un refus ne
     * consomme ni turn ni tirage** (SPEC-MOTEUR §2.2).
     *
     * Amendement **partagé avec ⟦A-44⟧** (le guide MeTube, gardé à 3 000). Absent ⇒ aucune
     * porte : c'est l'état de 39 nœuds sur 39, donc l'état neutre ⟦A-10⟧ par construction.
     *
     * ⚠️ Le nœud reste **visible et grisé** avec sa condition affichée — c'est une promesse,
     * et une promesse se voit ⟦A-43⟧.
     */
    abonnes_min: z.number().int().min(0).optional(),
    effets: z.array(Effect).default([]),
    assists: z.array(Assist).default([]),
    /**
     * ⟦C62 · lot arbre · A-179⟧ **Ce que le nœud rapporte, en UNE phrase de joueur** (12 mots au plus, aucun
     * identifiant, aucun jargon) : c'est la ligne que la carte garde à l'écran. Écrite à côté des `effets`
     * (rien n'est réécrit : les effets chiffrés se lisent dans le détail du nœud). Facultative : sans elle la
     * carte retombe sur le premier effet, comme avant le chantier.
     */
    effet_court: z.string().min(1).max(120).optional(),
})
    .strict();
/**
 * ⟦SPEC-PRODUCTEUR §11.4 · A-66⟧ **`content/producteur.json`** — les prix, les noms et les mots
 * de l'escalier de délégation. **Les règles de décision restent dans le moteur** (mécanique,
 * testable) ; ce fichier ne porte que ce qui s'écrit et se relit sans recompiler.
 *
 * `manager` est **optionnel** parce que l'étage 3 n'est pas construit ⟦A-66⟧ : le jour où il
 * l'est, le bloc devient obligatoire et ce commentaire disparaît. Un fichier qui promettrait un
 * manager que rien ne lit serait exactement le défaut que l'audit des maillons traque.
 */
const MembreEquipe = z
    .object({
    nom: z.string().min(1),
    description: z.string().min(1),
    /**
     * ⟦C57 · lot 5b · A-174⟧ La présentation en UNE phrase de 12 mots au plus, écrite à côté de la
     * longue (rien n'est réécrit : la longue s'ouvre dans « Les dessous »). Facultative : sans elle
     * l'écran retombe sur la longue, un contenu d'avant le chantier se lit comme avant.
     */
    description_courte: z.string().min(1).max(120).optional(),
    salaire_jour: z.number().int().positive(),
    prime_retour: z.number().int().min(0),
})
    .strict();
/**
 * ⟦C8 · A-120⟧ Un candidat **producteur** recrutable — `MembreEquipe` + un `id` de sélection.
 * Le bloc `producteur` ci-dessous reste le défaut compat (absence de choix ⇔ ce candidat, un
 * état sauvegardé avant ce chantier garde le comportement d'aujourd'hui) ; il figure aussi,
 * à l'identique, comme l'un des candidats de ce vivier — même grille que le marché des
 * freelances (⟦A-4 E / A-86⟧), en plus sobre : le producteur ne score aucune étape, seuls le
 * salaire et la prime varient d'un candidat à l'autre.
 */
export const CandidatProducteur = z
    .object({
    id: Id,
    nom: z.string().min(1),
    description: z.string().min(1),
    /** ⟦C57 · lot 5b · A-174⟧ Même chose que `MembreEquipe.description_courte`. */
    description_courte: z.string().min(1).max(120).optional(),
    salaire_jour: z.number().int().positive(),
    prime_retour: z.number().int().min(0),
})
    .strict();
export const EquipeFile = z
    .object({
    producteur: MembreEquipe,
    /** ⟦M3.29⟧ L'étage 3 est construit : le bloc est REQUIS, et un lecteur existe (`manager.ts`). */
    manager: MembreEquipe,
    /**
     * ⟦C8 · A-120⟧ Le vivier des producteurs recrutables — 2 à 3 candidats, `producteur`
     * ci-dessus compris. **Optionnel** : un contenu qui l'omet n'offre aucun choix, le défaut
     * reste seul embauchable (l'état neutre de ce chantier).
     */
    candidats_producteur: z.array(CandidatProducteur).min(2).max(3).optional(),
    jours_grace: z.number().int().positive(),
    libelles: z
        .object({
        cap: z.record(z.string()),
        regime: z.record(z.string()),
        tendance: z.record(z.string()),
        /** ⟦§3.3 · M3.29⟧ Les cadrans du MANAGER — la cadence et la politique de repos. */
        cadence: z.record(z.string()),
        repos: z.record(z.string()),
        /** Les motifs d'arrêt, en français de l'univers — **ce que le joueur lira le plus**. */
        arret: z.record(z.string()),
    })
        .strict(),
    /**
     * ⟦§11.4 / §13.1 ✎ 18 · A-81⟧ **Les gabarits de la ligne quotidienne** du récapitulatif —
     * une phrase par jour délégué, tirée de cette liste (jour modulo longueur, aucun tirage),
     * avec les trous `{jour}`, `{abonnes}`, `{argent}`, `{energie}`, `{sm}`, `{videos}`.
     * La spec en exige ≥ 12 ; **optionnel ici** parce que le contenu est tenu par une autre
     * session : absent, l'écran Équipe écrit sa ligne « Jour N… » d'avant — l'état neutre.
     * Présent, il en faut douze : en dessous, le jour se répète trop vite pour une voix.
     */
    journal: z.array(z.string().min(1)).min(12).optional(),
    /**
     * ⟦C57 · lot 5b · A-174⟧ **Les libellés COURTS des cadrans**, écrits à côté des longs de
     * `libelles` (rien n'est réécrit : le long reste le survol du bouton et s'ouvre dans « Les
     * dessous »). Un mot, deux au plus, avec son pictogramme : un choix tient en 6 mots. Mêmes clés
     * que `libelles` ; une clé absente retombe sur le long, un contenu d'avant le chantier se lit
     * comme avant.
     */
    libelles_courts: z
        .object({
        cap: z.record(z.string()),
        regime: z.record(z.string()),
        tendance: z.record(z.string()),
        cadence: z.record(z.string()),
        repos: z.record(z.string()),
        /**
         * ⟦C62 · lot 4⟧ **Les motifs d'arrêt en une demi-phrase**, la fin de « L'équipe a tenu 3 jours :
         * 2 vidéos, +1 511 abonnés. Un événement t'attend. » (le retour de « Laisser tourner », au toast et
         * sous le bouton). Mêmes clés que `libelles.arret` ; une clé absente retombe sur « On a besoin de
         * toi. », et sans ce bloc le retour s'arrête aux chiffres : un contenu d'avant le chantier se lit
         * comme avant. Le long reste ce qu'ouvre « Les dessous ».
         */
        arret: z.record(z.string()).optional(),
    })
        .strict()
        .optional(),
    /**
     * ⟦C57 · lot 5b · A-174⟧ Les gabarits de la ligne quotidienne en forme COURTE (12 mots au plus
     * une fois les trous remplis), **dans le même ordre que `journal`** : le jour N tire le même
     * rang dans les deux listes. Le long reste dans le contenu, il est relu dans « Les dessous ».
     */
    journal_court: z.array(z.string().min(1)).min(12).optional(),
})
    .strict();
export const SkillsFile = z.array(Skill).min(1);
/**
 * ⟦SPEC-PRODUCTEUR §2.1 / A-66⟧ Les capacités de l'**escalier de délégation**, celles que
 * `meta.equipe_active` commande. Liste fermée.
 *
 * ⚠️ `delegation_montage` et `delegation_totale` (étage 1, le marché des freelances) n'en font
 * **pas** partie et ne doivent jamais y tomber : elles tournent depuis M2.10, et un
 * interrupteur qui les éteindrait retirerait au joueur une mécanique livrée.
 */
export const CAPACITES_ESCALIER_DELEGATION = ["production_deleguee"];
/**
 * 🔑 **Le filtre des interrupteurs de contenu, écrit UNE fois.** ⟦A-66⟧
 *
 * Il a **quatre** appelants, et c'est exactement pourquoi il vit ici plutôt que recopié chez
 * chacun : `shared/src/content/load.ts` (le serveur), `client/src/game/content.ts` (le bac à
 * sable), `scripts/simulate.ts` (le harnais de mesure) et `determinism.test.ts`.
 *
 * ⚠️ **Les deux derniers sont la raison d'être de cette fonction.** `simulate.ts` et
 * `determinism.test.ts` ne passent PAS par le loader : ils lisent `content/*.json` au
 * `readFileSync`. Un interrupteur implémenté dans le seul loader aurait donc laissé le harnais
 * mesurer une mécanique que la production n'expose pas — le symétrique exact du défaut trouvé
 * quatre fois dans ce projet (« le harnais était aveugle »), et tout aussi silencieux. Ici il
 * aurait rendu FAUSSE la preuve d'état neutre elle-même, ce qui est pire : on aurait conclu
 * « rien n'a bougé » d'une campagne où le nœud était bel et bien achetable.
 *
 * Générique et structurel : accepte aussi bien le `Skill` du schéma que la vue élargie
 * d'`EngineContent.skills`, dont les `effets` sont typés `readonly unknown[]`.
 */
export function filtrerSkillsInterrupteurs(skills, meta) {
    /**
     * ⟦A-71⟧ Les interrupteurs sont **indépendants**, et le sont restés exprès : l'escalier de
     * délégation ⟦A-66⟧ et le guide MeTube ⟦A-44⟧ n'ont rien à voir l'un avec l'autre, et
     * Mathieu doit pouvoir allumer l'un sans l'autre. Un seul drapeau pour deux mécaniques
     * serait une décision prise à sa place.
     */
    const capacitesEteintes = new Set();
    if (meta?.equipe_active !== true) {
        for (const c of CAPACITES_ESCALIER_DELEGATION)
            capacitesEteintes.add(c);
    }
    // ⟦A-77 §4 · M3.29⟧ **L'étage 3 a son PROPRE interrupteur.** L'avenant l'exige mot pour
    // mot : si l'étage 2 devient allumable et que le manager n'est pas prêt, « on sépare les
    // interrupteurs plutôt que d'allumer un étage inachevé ». Et un manager sans producteur ne
    // tient le calendrier de rien : éteindre l'équipe éteint AUSSI le manager.
    if (meta?.manager_actif !== true || meta?.equipe_active !== true) {
        capacitesEteintes.add("rythme_delegue");
    }
    if (meta?.guide_actif !== true)
        capacitesEteintes.add("guide_metube");
    if (capacitesEteintes.size === 0)
        return skills;
    return skills.filter((s) => !s.effets.some((e) => {
        if (typeof e !== "object" || e === null)
            return false;
        const effet = e;
        return (effet.type === "unlock" &&
            typeof effet.capability === "string" &&
            capacitesEteintes.has(effet.capability));
    }));
}
// ─────────────────────────────────────────────────────────────────────────────
// §3.3 events.json
// ─────────────────────────────────────────────────────────────────────────────
/** Cooldown propre par défaut quand `cooldown_h` est absent : 480 h simulées ✎
 *  (SPEC-MOTEUR §10.5 — appliqué par le moteur, pas matérialisé dans le contenu). */
export const EVENT_COOLDOWN_H_DEFAUT = 480;
export const EventConditions = z
    .object({
    subs_min: z.number().int().min(0).optional(),
    subs_max: z.number().int().min(0).optional(),
    flags_requis: z.array(Id).default([]),
    flags_interdits: z.array(Id).default([]),
    jauges: z
        .array(z
        .object({
        gauge: GaugeId,
        min: z.number().optional(),
        max: z.number().optional(),
    })
        .strict())
        .default([]),
    image_min: z.number().min(-100).max(100).optional(), // image de chaîne (PLAN §4.7)
    image_max: z.number().min(-100).max(100).optional(),
    execution_min: z.number().min(0).max(100).optional(), // dernier score d'exécution (PLAN §4.5)
    execution_max: z.number().min(0).max(100).optional(),
    /**
     * ⟦SPEC-AXE-PRODUIT §6.1 · A-4 B⟧ **Les deux seules portes de l'axe Produit ↔ Authentique
     * vers le contenu** (avec les drapeaux `derive_produit` / `derive_artisanale`).
     *
     * Absentes = l'événement est ouvert à tout le monde, c'est-à-dire l'état de la totalité du
     * contenu d'avant l'axe : l'état neutre ⟦A-10⟧ par construction.
     *
     * ⚠️ Présentes, elles rendent l'événement **inéligible tant que l'axe est inactif**
     * (`produit_authentique === null`, c'est-à-dire avant le Grand Saut ⟦A-1⟧ ou interrupteur
     * éteint). Un procès en « il a changé » avant que l'argent n'existe n'aurait aucun sens.
     *
     * 🔑 L'axe est **lu** ici, jamais **écrit** par un effet : SPEC-AXE-PRODUIT §6.3 refuse
     * tout nouveau type d'effet. Il est dérivé — l'écrire depuis le contenu le rendrait faux,
     * et c'est exactement ce qui distingue cet axe de l'image de chaîne, qui a dû recevoir un
     * `channel_image_offset` ⟦A33⟧ pour que les comportements hors publication aient une prise.
     */
    produit_min: z.number().min(-100).max(100).optional(),
    produit_max: z.number().min(-100).max(100).optional(),
    /**
     * ⟦A-4 décision 2 · A-27⟧ **La condition de genre.** Absent = l'événement est **ouvert à
     * tout le monde** — c'est l'état de la totalité du contenu d'avant l'avenant, donc l'état
     * neutre : un événement sans `genre_requis` se tire EXACTEMENT comme avant.
     *
     * Présent (`"f"` ou `"h"`) = l'événement est réservé à ce genre. Une run **sans genre**
     * (`genre: null`) n'en satisfait aucun : elle joue le jeu d'avant, ce qui protège les
     * ancres de calibrage ⟦A-10⟧.
     *
     * Les 10 événements en miroir de `docs/EVENEMENTS-GENRES.md` §B sont livrés en **paires**
     * (`<id>_f` / `<id>_h`) : deux entrées, un `genre_requis` chacune, les MÊMES effets chiffrés
     * de part et d'autre sauf les trois cas ⚖️ où l'asymétrie EST le message (E3, E8, E10).
     * Le schéma « un id, deux blocs de textes » évoqué en note d'implémentation du document a
     * été écarté : il aurait exigé un chemin de rendu de texte genré dans le moteur ET dans
     * l'API, là où la paire ne demande qu'un filtre de candidats.
     */
    genre_requis: GenreId.optional(),
    /**
     * ⟦SPEC-MOTEUR §7.4 · A-4 C⟧ **La condition de niche** — l'événement n'est candidat que si
     * la chaîne est reconnue dans l'un de ces thèmes.
     *
     * Elle répare une impossibilité nommée par ⟦A-27⟧ et reconduite par ⟦A-53⟧ : *« E5 “actif
     * si niche gaming/tech” — `EventConditions` n'a aucun champ de thème, l'événement est livré
     * sans sa condition de niche »*. Il y en a maintenant un.
     *
     * **Liste, pas valeur unique** : le document d'origine demande « gaming **ou** tech »
     * d'un côté et « lifestyle / beauté / cuisine » de l'autre. Un seul thème par condition
     * aurait obligé à dupliquer l'événement autant de fois qu'il a de niches acceptables.
     *
     * Absente = ouvert à tout le monde, c'est-à-dire l'état de la totalité du contenu d'avant
     * l'avenant : l'état neutre ⟦A-10⟧ par construction. Présente, elle écarte aussi les
     * chaînes **sans** niche (touche-à-tout) — ce qui est le sens voulu : « pas pour toi, cette
     * niche » n'a rien à dire à quelqu'un qui n'en a pas.
     */
    niche_requise: z.array(ThemeId).min(1).optional(),
})
    .strict();
// ─────────────────────────────────────────────────────────────────────────────
// ⟦C57 · lot 2 · A-174⟧ Les textes COURTS des événements, écrits à côté des longs
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦C57 · lot 2 · A-174⟧ **Moins de texte : le court s'écrit À CÔTÉ du long, jamais à sa place.**
 *
 * Matt, 02/10/2026 : *« Il y a trop de trucs écrits partout, on ne lit plus rien à la fin […]
 * ne mettre que les mots ou petites phrases courtes importantes. »* La fenêtre d'événement est le
 * dialogue le plus vu du jeu : à l'écran, un titre de 5 mots au plus, une situation de 12 mots au
 * plus, un choix de 6 mots au plus, une morale de 12 mots au plus. Le texte long (`nom`, `texte`,
 * `label`, `morale`) ne bouge pas d'une virgule : il s'ouvre derrière « Les dessous » et rejoint
 * le Journal. **Aucun texte n'est supprimé, il change de place.**
 *
 * Les quatre champs sont facultatifs : un événement sans champ court se lit comme avant (l'écran
 * retombe sur le long). Un champ court n'est écrit que si le long dépasse le seuil, et un test
 * (`shared/src/content/evenements-courts.test.ts`) le vérifie dans les deux sens. Le mètre est
 * celui de `client/src/game/dessous.ts` et de la mesure sur le DOM : un « mot » est un jeton de
 * texte qui contient au moins une lettre ou un chiffre (les emojis, la ponctuation isolée et le
 * tiret ne comptent pas ; « 1 245 » ne fait qu'un mot).
 */
export const EVENT_COURT_MOTS_MAX = { titre: 5, texte: 12, label: 6, morale: 12 };
const JETON_EVT = /[^\s\u00a0\u202f]+(?:[\u00a0\u202f]\d+)*/gu;
const A_UN_MOT_EVT = /[\p{L}\p{N}]/u;
/** Le nombre de mots d'un texte d'événement (même mètre que l'écran, voir ci-dessus). */
export function compterMotsEvt(texte) {
    let n = 0;
    for (const jeton of texte.matchAll(JETON_EVT))
        if (A_UN_MOT_EVT.test(jeton[0]))
            n += 1;
    return n;
}
/** Un texte court : non vide, `max` mots au plus, sans tiret cadratin ni demi-cadratin. */
function TexteCourtEvt(max) {
    return z
        .string()
        .min(1)
        .refine((t) => compterMotsEvt(t) <= max, { message: `texte court de plus de ${max} mots` })
        .refine((t) => !/[\u2013\u2014]/u.test(t), { message: "tiret cadratin ou demi-cadratin" });
}
export const EventChoice = z
    .object({
    id: Id,
    label: z.string().min(1),
    skill_requis: Id.optional(), // choix visible seulement si le skill est possédé
    flag_requis: Id.optional(), // choix visible seulement si le flag est posé
    /**
     * ⟦SPEC-UI §3.6⟧ **Ce que ce choix télégraphie, en français** — « Les effets d'un choix ne
     * sont affichés QUE s'ils sont fournis par l'API (`hint`) ; le contenu décide de ce qui
     * est télégraphié ». ⟦A-49⟧ confirme la règle sans la lever.
     *
     * C'est une PHRASE de jeu (« Ta réputation en prendra un coup »), jamais un chiffre ni un
     * id d'effet : ⟦A-43⟧ interdit la ligne de wiki, et un chiffre recopié des `effets`
     * dériverait au premier rééquilibrage. Absent (le cas de tous les choix aujourd'hui) ⇒
     * rien ne change à l'écran — l'état neutre ⟦A-10⟧ par construction.
     */
    hint: z.string().min(1).optional(),
    /**
     * ⟦C57 · lot 2 · A-174⟧ Le libellé COURT du bouton (6 mots au plus), écrit à côté de `label`
     * quand celui-ci dépasse 6 mots. Le long reste intact : il s'ouvre derrière « Les dessous »
     * et se lit dans le Journal. Absent ⇒ l'écran affiche `label`, comme avant le chantier.
     */
    label_court: TexteCourtEvt(EVENT_COURT_MOTS_MAX.label).optional(),
    effets: z.array(Effect).min(1),
})
    .strict();
export const GameEvent = z
    .object({
    id: Id,
    categorie: EventCategory,
    nom: z.string().min(1),
    /** ⟦C57 · lot 2 · A-174⟧ Le titre COURT (5 mots au plus), à côté de `nom` quand celui-ci en
     *  compte plus de 5. Absent ⇒ l'écran affiche `nom`. */
    titre_court: TexteCourtEvt(EVENT_COURT_MOTS_MAX.titre).optional(),
    texte: z.string().min(1),
    /** ⟦C57 · lot 2 · A-174⟧ La situation COURTE (12 mots au plus), à côté de `texte` quand celui-ci
     *  en compte plus de 12. Absent ⇒ l'écran affiche `texte`. Le long s'ouvre derrière « Les
     *  dessous » et se relit dans le Journal. */
    texte_court: TexteCourtEvt(EVENT_COURT_MOTS_MAX.texte).optional(),
    poids: z.number().int().min(1).max(12), // échelle 1-12 ✎ (test T5, arbitrage A7)
    declencheur: TriggerId,
    tirable: z.boolean().default(true), // false = jamais au tirage pondéré (viralité = jet 3 % de la
    // formule §4.4 ; cibles d'event_schedule)
    negatif: z.boolean(), // true ⇒ contremesure OBLIGATOIRE (PLAN §4.9, test T4)
    conditions: EventConditions.default({
        flags_requis: [],
        flags_interdits: [],
        jauges: [],
    }),
    image: z.string().optional(), // chemin d'asset d'illustration (M4)
    /**
     * ⟦A-53⟧ **Le champ est OPTIONNEL, et son absence est le seul aiguillage.**
     *
     * Présent (le cas de 93 des 101 événements) ⇒ **rien ne change** : l'événement se pose en
     * `pending_event` bloquant, la fenêtre s'ouvre, le joueur tranche. C'est l'état neutre
     * ⟦A-10⟧, et il est obtenu **par construction** : le moteur ne lit `issue` que quand
     * `choix` est absent.
     *
     * Absent ⇒ l'événement est une **notification** : il porte une `issue` unique, ses effets
     * s'appliquent au tirage, il écrit sa ligne au journal et au récapitulatif ⟦A-16⟧, et il
     * **n'interrompt jamais**. SPEC-PRODUCTEUR §10.5 levier 3 : « un événement sans choix
     * viole le critère du §5.1 s'il interrompt — donc il ne doit pas interrompre du tout ».
     */
    choix: z.array(EventChoice).min(1).max(4).optional(),
    /**
     * ⟦A-53⟧ **L'issue d'un événement-notification** — obligatoire ssi `choix` est absent,
     * interdite sinon (voir le `refine` en bas de ce schéma).
     *
     * `label` n'est plus un bouton : c'est **ce que le personnage a fait**, écrit à la
     * troisième personne parce qu'il se lit APRÈS coup, dans le journal, à la suite de
     * `texte`. `effets` sont ceux de la branche retenue — un événement-notification garde
     * donc exactement le poids mécanique qu'il avait.
     */
    issue: z
        .object({ label: z.string().min(1), effets: z.array(Effect).min(1) })
        .strict()
        .optional(),
    contremesure: z
        .object({
        skill_id: Id, // DOIT exister dans skills.json (test T3)
        poids_mult: z.number().min(0).max(1), // ×poids si le skill est possédé (1.0 = pas de réduction)
        choix_debloque: Id.optional(), // id d'un choix de CET événement dont skill_requis = skill_id
    })
        .strict()
        .optional(),
    // ── Arbitrage A9 — les 3 champs attendus par SPEC-MOTEUR §10, absents de CONTENU §3.3 ──
    /** Cooldown propre de l'événement, en heures simulées (SPEC-MOTEUR §10.5, filtre des
     *  candidats au pas 3 de §10.1). Absent ⇒ le moteur applique `EVENT_COOLDOWN_H_DEFAUT`
     *  (480 h ✎). */
    cooldown_h: z.number().positive().optional(),
    /** Événement « majeur » : +1 PC « événement majeur survécu » à sa résolution complète
     *  (SPEC-MOTEUR §10.1 pas 9, §9.3). */
    major: z.boolean().default(false),
    /** Appartient au pool anti-frustration « fenêtre » (SPEC-MOTEUR §10.4) : au sommeil, si
     *  aucun palier depuis 336 h ✎ ET croissance hebdo < 1 % ✎, le tirage est FORCÉ sur ce
     *  pool (poids normaux, modulateurs sautés — §10.1 pas 2). */
    window: z.boolean().default(false),
    // ── ⟦A-15 §4⟧ Les opportunités qui expirent ──────────────────────────────────
    /** ⟦A-15 §4⟧ **Fenêtre de réponse, en heures simulées.** Absent ⇒ l'événement est un
     *  `pending_event` bloquant (SPEC-UI §3.6, arbitrage A24), comportement d'avant l'avenant.
     *  Présent ⇒ l'offre reste sur la table sans bloquer le jeu et se referme d'elle-même :
     *  collab, sponsor, invitation. Le moteur ne connaît QUE ce champ pour distinguer les
     *  deux — couper la mécanique, c'est retirer `fenetre_h` du contenu, rien d'autre.
     *  ⚠️ À ne pas confondre avec `window` ci-dessus (pool anti-frustration §10.4). */
    fenetre_h: z.number().positive().optional(),
    // ── ⟦A-4 décision 2 · A-27⟧ La pondération différenciée par genre ────────────
    /**
     * **Multiplicateur de poids par genre** — la « pondération différenciée » qu'A-4 décision 2
     * exige, et que la fiche de l'écran de création affiche au joueur (×1,5, ×1,3, ×0,7).
     *
     * C'est le champ qui permet à `docs/EVENEMENTS-GENRES.md` de tenir sa règle 5 : **mêmes
     * effets chiffrés des deux côtés, mais pas la même probabilité de croiser l'événement**.
     * Une créatrice croise « Tout sauf la vidéo » une fois et demie plus souvent qu'un neutre ;
     * un créateur, deux fois moins souvent qu'elle — sans qu'aucun des deux ne subisse un malus
     * chiffré différent quand il tombe.
     *
     * **État neutre ⟦A-10⟧, dans les deux sens** : absent ⇒ ×1 pour tout le monde ; présent
     * ⇒ **seule la clé qui correspond au genre de la run s'applique**, donc une run
     * `genre: null` reste à ×1 quoi qu'il arrive. Le champ ne peut pas déplacer une ancre.
     *
     * La paire complète est déclarée sur les DEUX moitiés d'un événement en miroir : la seule
     * clé qui compte pour une moitié est la sienne, mais lire `{ f: 1.5, h: 0.7 }` sur les deux
     * entrées, c'est lire l'événement comme UN réglage — et voir du même coup que la fiche de
     * l'écran de création dit vrai.
     *
     * `0` est autorisé (l'événement ne sort plus jamais pour ce genre), comme
     * `contremesure.poids_mult`.
     */
    poids_genre: z
        .object({ f: z.number().min(0).optional(), h: z.number().min(0).optional() })
        .strict()
        .optional(),
    /**
     * ⟦A-27⟧ **La petite phrase pédagogique** — règle d'écriture 6 de
     * `docs/EVENEMENTS-GENRES.md` : « une par événement, en italique à la fin du texte de
     * résolution, courte, jamais un sermon ».
     *
     * Elle a son propre champ parce qu'elle ne se dit pas au même moment que `texte` : `texte`
     * pose la situation AVANT le choix, la morale tombe APRÈS. La coller dans `texte` aurait
     * livré la leçon avant la décision — ce qui la transforme en consigne.
     *
     * Optionnel, et absent partout ailleurs : l'état neutre est « pas de morale ».
     */
    morale: z.string().min(1).optional(),
    /**
     * ⟦C57 · lot 2 · A-174⟧ La morale COURTE (12 mots au plus), à côté de `morale` quand celle-ci en
     * compte plus de 12 : c'est elle que dit la fenêtre « c'est réglé ». La morale longue, intacte,
     * s'ouvre derrière « Les dessous » et rejoint le Journal. Exige `morale`.
     */
    morale_courte: TexteCourtEvt(EVENT_COURT_MOTS_MAX.morale).optional(),
    /**
     * ⟦SPEC-CONTENU §5bis.2 · A-4 décision 16⟧ **La micro-interaction** : un formulaire absurde
     * (QCM administratif) qui casse la routine des choix à boutons. **Ce n'est PAS un mini-jeu
     * d'adresse** : aucun score d'Atelier, aucune exigence de palier, aucune variante
     * d'accessibilité lourde — le composant générique (`client/src/components/`) n'anime rien
     * qu'un `<select>` habillé.
     *
     * `decor` porte des questions purement décoratives — leur réponse ne part nulle part, aucun
     * effet, aucun état écrit. Elles ne servent qu'à faire tourner la manivelle administrative
     * avant la vraie question. **La dernière question EST `choix`** : le composant affiche les
     * libellés de `choix[]` comme les options de ce dernier champ du formulaire, et choisir en
     * résout un exactement comme un clic sur le bouton normal — même `choix_evenement`, mêmes
     * effets. Pas de mapping séparé à tenir à jour : un `choix` ajouté au tableau apparaît de
     * lui-même dans le formulaire.
     *
     * Optionnel, absent de tout le contenu livré sauf un événement témoin : l'état neutre
     * ⟦A-10⟧ est total — un événement sans micro-interaction se rend exactement comme avant ce
     * champ, et le moteur ne le lit jamais (c'est un maillon écran pur, `choix_evenement` ne
     * change pas de forme). Voir le refine ci-dessous : exige `choix` (pas `issue`), pour la
     * même raison qu'une offre exige un choix.
     */
    micro_interaction: z
        .object({
        type: z.literal("qcm"),
        /** L'en-tête du formulaire — le nom bureaucratique qui fait sourire. */
        entete: z.string().min(1),
        decor: z
            .array(z.object({
            question: z.string().min(1),
            options: z.array(z.string().min(1)).min(2).max(4),
        }))
            .min(1)
            .max(3),
    })
        .strict()
        .optional(),
})
    .strict()
    .refine((e) => !e.negatif || e.contremesure !== undefined, {
    message: "événement négatif sans contre-mesure (PLAN §4.9)",
})
    .refine((e) => e.morale_courte === undefined || e.morale !== undefined, {
    message: "`morale_courte` sans `morale` : le court s'écrit à côté du long, jamais seul (⟦C57⟧)",
})
    .refine((e) => e.micro_interaction === undefined || e.choix !== undefined, {
    message: "`micro_interaction` sans `choix` : le dernier champ du formulaire n'aurait rien à proposer",
})
    // ⟦A-53⟧ Exactement l'un des deux. Sans cette borne, un événement sans `choix` ET sans
    // `issue` serait un événement muet et sans effet — et un événement qui porte les deux
    // laisserait le moteur choisir en silence lequel des deux compte.
    .refine((e) => (e.choix === undefined) !== (e.issue === undefined), {
    message: "un événement porte SOIT `choix` (il interrompt et le joueur tranche), SOIT `issue` " +
        "(notification : il s'applique et part au récapitulatif) — jamais les deux, jamais aucun",
})
    // ⟦A-53 · A-15 §4⟧ Une offre EST une décision : `fenetre_h` ne veut rien dire sans choix.
    .refine((e) => e.choix !== undefined || e.fenetre_h === undefined, {
    message: "`fenetre_h` sur un événement-notification : une offre sans choix n'a pas de sens",
})
    // ⟦A-53⟧ `contremesure.choix_debloque` désigne un choix de CET événement : une notification
    // n'en a plus. La contre-mesure elle-même (le `poids_mult`) reste licite et utile.
    .refine((e) => e.choix !== undefined || e.contremesure?.choix_debloque === undefined, {
    message: "`contremesure.choix_debloque` sur un événement-notification : il n'a plus de choix",
})
    .refine((e) => e.conditions.genre_requis === undefined ||
    e.conditions.flags_requis.includes(GENRE_FLAG[e.conditions.genre_requis]), {
    message: "événement genré sans son verrou : `conditions.genre_requis` doit être doublé du flag " +
        "moteur `genre_f`/`genre_h` dans `flags_requis` tant que `conditionsPassent` " +
        "(shared/src/engine/events.ts) ne lit pas `genre_requis` ⟦A-27⟧",
});
export const EventsFile = z.array(GameEvent).min(1);
/** Vocabulaire fermé des « tags » de SPEC-MOTEUR §10.3, pour mémoire : `good_buzz`,
 *  `bad_buzz`, `drama`, `opportunite` sont des valeurs de `EventCategory` ; `negatif` est le
 *  booléen `negatif` ; `window` et `major` sont les deux champs A9 ci-dessus. Aucun champ
 *  `tags` libre n'existe donc dans le contenu. */
// ─────────────────────────────────────────────────────────────────────────────
// rivaux.json — ⟦A-15 §3⟧ / M3.16 : les créateurs PNJ persistants
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦M3.16 · A-4⟧ Un créateur PNJ. Le backlog en demande « 2-3 par run (nom, niche, courbe
 * simulée, ton) », et ⟦A-15 §3⟧ ajoute le lien au temps : **il progresse pendant que le
 * joueur dort**. C'est la pression la plus forte du lot, parce que c'est la seule qui donne
 * un **visage** au temps perdu.
 *
 * ⚠️ **Courbe SIMULÉE, pas une run parallèle** (M3.16, mot pour mot). Le contenu ne décrit
 * donc pas un comportement mais trois **fourchettes** : d'où il part, à quelle vitesse il
 * monte, où il plafonne. Le moteur y tire un point par run — même seed ⇒ mêmes PNJ.
 *
 * Chaque borne est une paire `[min, max]` : le tirage s'y fait uniformément. Poser
 * `min === max` fige la valeur, ce qui rend un PNJ scénarisé possible sans schéma dédié.
 */
const Fourchette = z
    .tuple([z.number(), z.number()])
    .refine(([min, max]) => min <= max, { message: "fourchette inversée : min > max" });
export const Rival = z
    .object({
    id: Id,
    /** Nom d'affichage. Univers **parodique** : jamais un vrai créateur (CLAUDE.md). */
    nom: z.string().min(1),
    /** Sa niche — un `ThemeId`, le même vocabulaire que les sujets et les tendances. */
    niche: ThemeId,
    /** Son ton, pour que les événements le fassent parler juste. */
    ton: z.string().min(1),
    /** Une ligne de présentation : ce qu'il poste, ce qu'on voit de lui. */
    description: z.string().min(1),
    /** Abonnés au premier jour de la run. */
    depart: Fourchette,
    /** Taux logistique **par jour simulé** — la pente de sa courbe. */
    vitesse: Fourchette,
    /** Là où il plafonne : tous les PNJ ne deviennent pas des stars. */
    plafond: Fourchette,
})
    .strict();
/** Au moins 3 PNJ dans le vivier : le moteur en tire 2 ou 3 **sans remise** par run. */
export const RivauxFile = z.array(Rival).min(3);
// ─────────────────────────────────────────────────────────────────────────────
// freelances.json — ⟦A-4 décision 4 / arbitrage E⟧ · M2.10 : le marché des freelances
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-45 règle 1⟧ **Plafond ABSOLU d'une cote de freelance, écrit dans le schéma.**
 *
 * *« Si un freelance était strictement meilleur que le joueur, personne ne jouerait plus les
 * mini-jeux. Les meilleurs coûtent cher, et restent en dessous d'un très bon joueur. »* Un
 * « très bon joueur » est la loi `N(88, 5)` du profil `forte` (`scripts/simulate.ts` §9.2) :
 * **82 est six points sous sa moyenne**, et un freelance ne peut donc jamais, par aucun
 * chemin, valoir mieux qu'une exécution forte.
 *
 * La règle vit **ici** et pas dans un commentaire du moteur : une cote à 95 écrite un soir
 * dans `freelances.json` ferait échouer le boot au lieu de vider les mini-jeux en silence.
 * La pépite est vérifiée séparément, cote **d'arrivée** comprise (voir `FreelancesFile`).
 */
export const COTE_FREELANCE_MAX = 82;
/**
 * Ce qu'une **pépite entièrement fidélisée** gagne au total, en points de cote —
 * `PEPITE_GAIN_PAR_EMBAUCHE × PEPITE_EMBAUCHES_MAX` de `engine/freelances.ts`, recopié ici
 * pour garder `content/` **libre du moteur** (même procédé que `TREND_MULT_MOTEUR` dans
 * l'autre sens). Un test verrouille l'égalité des deux sources.
 *
 * C'est le nombre sans lequel le plafond `COTE_FREELANCE_MAX` serait contournable **par le
 * temps** : une pépite écrite à 60 finirait à 90 sans qu'aucun `.max()` de champ ne bronche.
 */
export const COTE_PEPITE_GAIN_TOTAL = 30;
/**
 * Un freelance du marché (SPEC-MOTEUR §2.2, marché des freelances).
 *
 * Le contenu décrit **des gens**, pas un barème : un prix, une cote, une main plus ou moins
 * sûre (`variance`), et de quoi les faire exister à l'écran. Le moteur n'en dérive que trois
 * choses — qui est disponible, ce que ça coûte, ce que ça rend.
 */
export const Freelance = z
    .object({
    id: Id,
    /** Nom d'affichage. Univers **parodique** : jamais un vrai prestataire (CLAUDE.md). */
    nom: z.string().min(1),
    /** L'étape qu'il sait faire. Un freelance ne travaille QUE sur la sienne. */
    etape: EtapeAtelierId,
    /** Sa cote 0-100 : le score d'étape qu'il rend, avant variance. Plafonnée par règle. */
    cote: z.number().int().min(0).max(COTE_FREELANCE_MAX),
    /**
     * L'amplitude de sa main, en points : le score rendu est `cote ± variance` (uniforme).
     * Les pas chers tremblent, les chers sont réguliers — c'est **ça** qu'on paie.
     */
    variance: z.number().int().min(0).max(20),
    /** Prix de base d'une embauche, en Clicks. Le tarif réel suit la notoriété (moteur). */
    prix: z.number().int().min(0),
    /**
     * ⟦A-4 décision 4⟧ **La pépite** : un jeune pas cher dont la cote MONTE si on le garde,
     * et qui reste attaché à la chaîne (il ne quitte plus le marché une fois embauché).
     * C'est la seule personne du lot sur qui on peut compter — tout le reste tourne.
     */
    pepite: z.boolean(),
    /**
     * ⟦A-86⟧ **Le tarif d'entrée** : la personne qui est TOUJOURS au marché de son étape,
     * jamais tirée, jamais sortie — pas chère, et qui rend une vidéo moyenne, jamais une bonne
     * (*« prix bas ⇔ cote basse »*, décision de Mathieu du 22/08). C'est ce qui fait que le
     * premier marché, au Grand Saut, ne ruine pas un joueur prudent : il y a toujours de quoi
     * embaucher sans vider la caisse. Absent ⇒ `false` : une personne ordinaire, qui tourne.
     *
     * Exclusif avec `pepite` (l'une grandit et se fidélise, l'autre est là et ne bouge pas),
     * et **un seul par étape** — au-delà, le marché cesse de tourner (`load.ts`).
     */
    tarif_entree: z.boolean().default(false),
    /** Qui c'est, en une ou deux phrases. Le marché est une galerie de gens, pas un tableur. */
    description: z.string().min(1),
    /** Ce qu'il dit quand il apparaît au marché. */
    phrase: z.string().min(1),
})
    .strict();
/**
 * `content/freelances.json` — **optionnel** comme `boutique.json` et `rivaux.json` : un
 * vivier absent ⇒ marché vide ⇒ aucune embauche possible ⇒ exactement le jeu d'avant M2.10
 * (⟦A-10⟧, état neutre). Le boot ne tombe jamais pour un contenu en cours d'écriture.
 *
 * Trois invariants croisés, tous les trois là pour empêcher une dérive **silencieuse** :
 *  1. **≥ 2 freelances par étape ouverte** — un marché à une seule offre n'est pas un marché,
 *     c'est un bouton ; l'arbitrage prix/cote disparaîtrait sans que rien ne le dise.
 *  2. **≥ 1 pépite par étape** — c'est la pièce que ⟦A-4⟧ tient à voir exister partout.
 *  3. **cote d'ARRIVÉE d'une pépite ≤ `COTE_FREELANCE_MAX`** — une pépite fidélisée gagne
 *     `PEPITE_GAIN_PAR_EMBAUCHE × PEPITE_EMBAUCHES_MAX` points (`engine/freelances.ts`) ;
 *     sans cette vérification, la règle 1 d'⟦A-45⟧ se ferait contourner **par le temps**.
 *
 * ⟦A-86⟧ S'y ajoutent ceux du **tarif d'entrée** (`tarif_entree`) : jamais une pépite, au plus
 * un par étape, le moins cher de son étape, et **aucun non-pépite plus cher ne rend moins**
 * (« prix bas ⇔ cote basse » — sinon les nœuds qui approfondissent l'offre ne vendent rien).
 */
export const FreelancesFile = z.array(Freelance).min(1);
// ─────────────────────────────────────────────────────────────────────────────
// managers.json — ⟦C8 · A-120⟧ Le manager incarné : le vivier des managers recrutables
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦C8 §2⟧ Le STYLE d'un manager : lumière préfère les issues prudentes / réputation, chaos
 * les issues audacieuses / croissance, neutre prend le meilleur bilan brut — dérivé des
 * `effets` d'un choix par `engine/manager.ts`, jamais tagué en dur sur le contenu.
 */
export const AlignementManager = z.enum(["lumiere", "chaos", "neutre"]);
/** ⟦C8 §2⟧ La QUALITÉ : forte prend LA meilleure issue de son style, faible tire au sort, biaisé. */
export const ExperienceManager = z.enum(["faible", "forte"]);
/**
 * Un manager recrutable (`content/managers.json`) — même grille que le marché des freelances
 * (⟦A-4 E / A-86⟧, le précédent nommé par le brief), en plus sobre : pas de `cote`/`variance`,
 * l'alignement et l'expérience suffisent à dériver son comportement (`managerTranche`).
 */
export const CandidatManager = z
    .object({
    id: Id,
    /** Nom d'affichage. Univers **parodique** (CLAUDE.md). */
    nom: z.string().min(1),
    alignement: AlignementManager,
    experience: ExperienceManager,
    salaire_jour: z.number().int().positive(),
    prime_retour: z.number().int().min(0),
    description: z.string().min(1),
    /** ⟦C57 · lot 5b · A-174⟧ Même chose que `MembreEquipe.description_courte`. */
    description_courte: z.string().min(1).max(120).optional(),
    phrase: z.string().min(1),
})
    .strict();
/**
 * `content/managers.json` — **optionnel**, comme `freelances.json` : absent, aucun candidat à
 * recruter, `manager.ts` retombe sur le défaut compat (`producteur.json → manager`, neutre et
 * expérimenté) — exactement le manager abstrait d'avant ce chantier, au caractère près.
 */
export const ManagersFile = z.array(CandidatManager).min(1);
// ─────────────────────────────────────────────────────────────────────────────
// §3.4 subjects.json
// ─────────────────────────────────────────────────────────────────────────────
export const Subject = z
    .object({
    id: Id,
    nom: z.string().min(1),
    theme: ThemeId,
    alignement: z.number().int().min(-100).max(100), // lumière ≥ +30, neutre ]-30;+30[, chaos ≤ −30 ✎
    potentiel_evergreen: z.number().int().min(0).max(100), // éligible Evergreen si ≥ 60 ✎ (avec skill
    // L'Intemporelle + Qualité ≥ 1,3, PLAN §4.6)
    titres: z.array(z.string().min(1)).min(2), // titres « à la Maxime » proposés au putaclic-o-mètre
})
    .strict();
export const SubjectsFile = z.array(Subject).min(1);
// ─────────────────────────────────────────────────────────────────────────────
// §3.5 trends.json
// ─────────────────────────────────────────────────────────────────────────────
export const TREND_MULT = { emergente: 1.3, saturee: 0.9, morte: 0.6 }; // PLAN §4.8
export const DUREE_MORTE_H = 48; // ✎ heures simulées avant rotation du slot
export const Trend = z
    .object({
    id: Id,
    nom: z.string().min(1),
    themes: z.array(ThemeId).min(1), // sujets de ces thèmes = « sur la tendance »
    alignement: z.number().int().min(-100).max(100), // les dramas sont des tendances chaos (PLAN §4.8)
    mult_pic: z.number().min(2).max(2.5), // PLAN §4.8
    duree_emergente_h: z.number().positive(), // ✎
    duree_pic_h: z.number().min(48).max(80), // pic 3-5 jours simulés (PLAN §4.6)
    duree_saturee_h: z.number().positive(), // ✎
})
    .strict();
export const TrendsFile = z.array(Trend).min(12); // PLAN vigilance 9 : 12+ tendances
// ─────────────────────────────────────────────────────────────────────────────
// §3.6 milestones.json
// ─────────────────────────────────────────────────────────────────────────────
export const Milestone = z
    .object({
    id: Id,
    nom: z.string().min(1),
    /**
     * ⟦C57 · lot 5b · A-174⟧ Le titre COURT d'une fiche de jalon, écrit à côté du long (rien n'est
     * réécrit). « 6. » (le rang) compte pour un mot : 4 mots au plus ici pour que la ligne tienne en
     * 5. Facultatif : sans lui l'écran retombe sur `nom`.
     */
    nom_court: z.string().min(1).max(40).optional(),
    ordre: z.number().int().positive(),
    conditions: z
        .object({
        palier_min: z.number().int().min(0),
        skills: z.array(Id).default([]), // DOIVENT exister et être atteignables (test T7)
        flags_requis: z.array(Id).default([]),
        image_min: z.number().min(-100).max(100).optional(),
        /**
         * ⟦A-123 · C9 vague 0⟧ **Les cinq extensions de schéma validées par Mathieu**, chacune
         * un champ de condition optionnel — absent ⇔ comportement d'aujourd'hui, l'état neutre
         * par construction (CLAUDE.md). Nommées par `docs/CATALOGUE-VIDEOS-SPECIALES.md`
         * (tableau « Récapitulatif ») : seuil sur `videos_published`, seuil d'argent, seuil de
         * réputation, une durée, un jalon précédent déjà réussi.
         */
        /** Seuil sur `counters.videos_published` (fiches 10, 15 du catalogue). */
        videos_published_min: z.number().int().min(0).optional(),
        /** Seuil sur `state.money`, peut être franchi négativement en théorie (fiche 12). */
        money_min: z.number().optional(),
        /** Seuil sur `state.reputation`, 0-100 (fiche 31). */
        reputation_min: z.number().min(0).max(100).optional(),
        /**
         * Une durée — heures de calendrier simulé écoulées depuis le début de la partie
         * (`state.clock_h`, qui compte nuits comprises depuis ⟦A-19⟧). Fiches 9, 24, 26.
         */
        clock_h_min: z.number().positive().optional(),
        /** Un jalon précédent déjà RÉUSSI (`state.jalons_done`), jamais seulement tenté (fiche 36). */
        jalon_precedent: Id.optional(),
    })
        .strict(),
    atelier: z.enum(["standard"]), // v1 (PLAN §5) ; post-v1 : ateliers spéciaux
    format: z.enum(["longue"]), // tous les jalons sont des longues
    classe_forcee: z.enum(["evergreen"]).optional(), // le Documentaire (PLAN §4.13)
    effets_reussite: z
        .object({
        subs_pct_min: z.number().min(0).max(100),
        subs_pct_max: z.number().min(0).max(100),
        autres: z.array(Effect).default([]),
    })
        .strict(),
    effets_echec: z.array(Effect).default([]),
})
    .strict();
export const MilestonesFile = z.array(Milestone).min(10);
// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-11 §4⟧ boutique.json — le matériel de VIE
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Paliers d'abonnés du jeu (PLAN §4.13) — recopiés ici parce que `content/schema.ts` ne doit
 * pas dépendre de `engine/types.ts` (la dépendance va dans l'autre sens : le moteur lit le
 * contenu). Le test de cohérence T-boutique vérifie qu'ils coïncident.
 */
export const PALIERS_CONTENU = [100, 1_000, 10_000, 100_000, 500_000, 1_000_000];
/**
 * Un objet de boutique ⟦A-11 §4⟧.
 *
 * **Ce que la boutique N'EST PAS** : l'échelle de matériel de tournage. Celle-là existe déjà,
 * c'est la branche Tournage de l'arbre (webcam → ring light → setup → B-roll → multicam →
 * plateau, en PC + Clicks, PLAN §252). Ici on n'achète rien pour filmer : on achète de quoi
 * **vivre**, et donc de quoi tenir le rythme. La collab fait entrer l'argent, la boutique le
 * fait sortir.
 */
export const BoutiqueObjet = z
    .object({
    id: Id,
    nom: z.string().min(1),
    description: z.string().min(1),
    /** Enum fermé : un meuble se garde, une boisson se boit. */
    categorie: z.enum(["permanent", "consommable"]),
    /** En euros dans les données, affiché en Clicks (SPEC-CONTENU §1.2). */
    prix_clicks: z.number().int().positive(),
    /**
     * Palier d'abonnés à partir duquel l'objet est visible et achetable.
     *
     * **≥ 1 000 obligatoire** : avant le Grand Saut, la jauge Argent est INACTIVE (⟦A-1⟧,
     * `effects.ts` ignore tout `gauge_delta` sur `argent`). Une boutique ouverte plus tôt
     * serait une vitrine sans porte — et si Mathieu veut la faire respirer avant, c'est le
     * Grand Saut qu'il faut déplacer, pas cette borne.
     */
    palier_min: z
        .number()
        .int()
        .refine((p) => PALIERS_CONTENU.includes(p), {
        message: "palier_min doit être un palier du jeu (100 / 1 000 / 10 000 / 100 000 / 500 000 / 1 000 000)",
    })
        .refine((p) => p >= 1_000, {
        message: "palier_min ≥ 1 000 : l'argent n'entre qu'au Grand Saut ⟦A-1⟧",
    }),
    /** Autres objets de CE fichier, **permanents uniquement** (invariant vérifié au boot). */
    prerequis: z.array(Id).default([]),
    /**
     * Consommables seulement — quand l'effet a lieu. `immediat` : tout de suite, à l'achat.
     * `prochaine_nuit` : au prochain sommeil, une seule fois (`state.inventaire.nuit_prochaine`).
     *
     * ⟦A-60⟧ `prochaine_prod` : le **matos de la vidéo qui compte**. Ses `effets` (le prix en
     * jauges — préparer coûte) s'appliquent **à l'achat**, comme `immediat` ; ses `assists`
     * attendent dans `state.inventaire.prochaine_prod` et arment **la prochaine production**,
     * une seule fois. C'est la case « temporaire et fort » de la grille ⟦A-50 ter⟧, qui était
     * vide : 16 aides permanentes et faibles dans l'arbre, **zéro** aide brève et forte à
     * l'achat.
     */
    moment: z.enum(["immediat", "prochaine_nuit", "prochaine_prod"]).default("immediat"),
    /** Une phrase pour l'UI — le client n'a pas à re-dériver un texte depuis les effets. */
    effet_lisible: z.string().min(1),
    /**
     * ⟦C57 · lot 5a · A-174⟧ Les formes COURTES de la carte, écrites à côté des longues (rien n'est
     * réécrit : le long s'ouvre dans « Les dessous »). Toutes facultatives : sans elles, l'écran
     * retombe sur le long, un contenu d'avant le chantier se lit comme avant.
     *  - `nom_court` : le titre de la carte (4 mots au plus, pour que « Acheter « … » ? » tienne en 5) ;
     *  - `description_courte` : UNE phrase de 12 mots au plus (le survol de la carte) ;
     *  - `effet_court` : l'effet en une ligne chiffrée (12 mots au plus), sans les parenthèses du calcul.
     */
    nom_court: z.string().min(1).max(40).optional(),
    description_courte: z.string().min(1).max(120).optional(),
    effet_court: z.string().min(1).max(100).optional(),
    effets: z.array(Effect).min(1),
    /**
     * ⟦A-60⟧ Aides de mini-jeu portées par l'objet — **même forme que celles d'un nœud**
     * (`Skill.assists`), pour que `collectModifiers` les agrège dans le même agrégat et que
     * `server/src/atelier/` n'ait pas une ligne à changer : un objet préparé se lit comme un
     * nœud possédé, en plus fort et pour une seule vidéo.
     *
     * Trois interdits, vérifiés juste en dessous :
     *  - **un permanent n'en porte pas** — un meuble qui aiderait pour toujours serait un nœud
     *    d'arbre déguisé, et viderait la branche qui vend déjà cette aide ⟦A-50 ter⟧ ;
     *  - **aucun param de `ASSIST_PARAMS_GESTE`** — un objet se possède, il ne s'apprend pas
     *    ⟦A-55 post-scriptum⟧ ; une phase est de la compétence, toujours ;
     *  - **pas d'assists sans `moment: "prochaine_prod"`**, et réciproquement : un assist qui
     *    n'a pas d'instant d'application n'agirait jamais — le contenu mort que le boot refuse.
     */
    assists: z.array(Assist).default([]),
})
    .strict()
    .superRefine((objet, ctx) => {
    const ajouter = (chemin, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: chemin, message });
    // ⟦A-60⟧ Le couple `moment` ⇄ `assists` : l'un sans l'autre ne fait rien.
    if (objet.assists.length > 0 && objet.moment !== "prochaine_prod") {
        ajouter(["moment"], `un objet qui porte des assists doit avoir moment: "prochaine_prod" — sinon ils ` +
            `n'ont aucun instant d'application (id: "${objet.id}")`);
    }
    if (objet.moment === "prochaine_prod" && objet.assists.length === 0) {
        ajouter(["assists"], `moment: "prochaine_prod" sans assists — l'objet ne ferait rien à la production ` +
            `(id: "${objet.id}")`);
    }
    if (objet.assists.length > 0 && objet.categorie !== "consommable") {
        ajouter(["categorie"], `seul un consommable peut porter des assists : une aide permanente est une COMPÉTENCE ` +
            `⟦A-50 ter⟧ (id: "${objet.id}")`);
    }
    objet.assists.forEach((a, i) => {
        if (!ASSIST_COMPAT[a.minigame].includes(a.param)) {
            ajouter([`assists`, i], `"${a.param}" incompatible avec le mini-jeu ${a.minigame}`);
            return;
        }
        // 🔑 ⟦A-55⟧ La SEULE exclusivité du système, et elle ne va que dans un sens.
        if (ASSIST_PARAMS_GESTE.includes(a.param)) {
            ajouter([`assists`, i], `"${a.param}" ajoute une PHASE : seule une compétence peut le faire ⟦A-55⟧ — ` +
                `un objet se possède, il ne s'apprend pas. Ce nœud a sa place dans skills.json.`);
        }
        if (ASSIST_BOOL_PARAMS.includes(a.param) && a.value !== 1) {
            ajouter([`assists`, i], `param booléen "${a.param}" à ${a.value} (attendu 1)`);
        }
    });
});
export const BoutiqueFile = z.array(BoutiqueObjet).min(1);
// ─────────────────────────────────────────────────────────────────────────────
// A11 — atelier.json (7e fichier) : valeurs de base des 5 mini-jeux (SPEC-UI §4.2)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Familles de scaling par niveau d'exigence E (SPEC-UI §4.1). Les bases sont données à
 * **E1** ; pour E ≥ 1, avec `k = E − 1` :
 *   - `tempo`        → × `tempo_mult^k`        (vitesses : BPM)
 *   - `duree_tempo`  → ÷ `tempo_mult^k`        (durées PILOTÉES par le tempo : traversée du
 *                       Tournage, période d'oscillation de la Miniature — cette famille
 *                       n'est pas nommée par §4.1 mais elle est EXIGÉE par la table §4.2)
 *   - `fenetre`      → × `fenetre_mult^k`      (fenêtres, chronos, largeurs de zone)
 *   - `cible`        → × `cible_mult^k`        (cuts requis, zones chaudes ; arrondi entier)
 *   - `fixe`         → jamais scalé
 * E0 (mode doux) applique les facteurs `e0` par rapport à E1.
 * La dérivation elle-même vit côté serveur (M2.8) : le contenu ne fournit que les bases,
 * les familles et — au besoin — la table explicite `valeurs_e`.
 */
export const AtelierScalingKind = z.enum([
    "tempo",
    "duree_tempo",
    "fenetre",
    "cible",
    "fixe",
]);
export const AtelierScalingRules = z
    .object({
    /** Paliers d'abonnés dont le franchissement incrémente E (SPEC-UI §4.1). */
    paliers: z.array(z.number().int().positive()).length(5),
    tempo_mult: z.number().positive(), // 1.08
    fenetre_mult: z.number().positive(), // 0.90
    cible_mult: z.number().positive(), // 1.20
    e0: z
        .object({
        tempo_mult: z.number().positive(), // 0.90
        fenetre_mult: z.number().positive(), // 1.30
        cible_mult: z.number().positive(), // 0.80
    })
        .strict(),
})
    .strict();
/**
 * Une valeur scalée : base à E1 + famille de scaling. `valeurs_e` permet de porter
 * LITTÉRALEMENT la ligne E0-E5 de la table SPEC-UI §4.2 (arbitrage A12 : « la table est
 * exécutable ») ; quand elle est présente, elle fait foi sur la dérivation — c'est ce qui
 * rattrape les lignes de la table que la formule §4.1 ne reproduit pas exactement.
 */
export const AtelierValeur = z
    .object({
    base_e1: z.number(),
    scaling: AtelierScalingKind,
    entier: z.boolean().default(false), // arrondi entier après scaling (cuts, zones)
    min: z.number().optional(), // plancher après arrondi (SPEC-UI §4.1 : « minimum 1 »)
    valeurs_e: z.array(z.number()).length(6).optional(), // E0..E5 explicites (§4.2)
})
    .strict();
/** Plateformes de la Découpe — noms PARODIQUES (arbitrage A15, CLAUDE.md). */
export const PlatformId = z.enum(["toktok", "instapix", "metube_shorts"]);
/** Type d'un moment fort de la heatmap (SPEC-UI §5.5). */
export const HeatZoneType = z.enum(["humour", "wow", "info"]);
/**
 * ⟦C52 · A-170 §1⟧ Une phrase de bloc : courte (elle tient en quatre lignes dans une colonne de
 * 80 px sur un téléphone), dans l'univers parodique, **jamais le nom de la chaîne parodiée**.
 */
const PhraseDeBloc = z
    .string()
    .min(3)
    .max(34)
    .refine((t) => !/maxime\s*click|maximeclick|peiffer/i.test(t), {
    message: "une phrase de bloc ne nomme jamais Maxime Click (univers parodique)",
})
    // Une colonne de téléphone fait 80 px : un mot de plus de 11 caractères y serait coupé en deux.
    .refine((t) => t.split(/\s+/).every((mot) => mot.length <= 11), {
    message: "aucun mot d'une phrase de bloc ne dépasse 11 caractères (colonne de téléphone)",
});
/** Au moins 12 phrases distinctes : la manche en tire une dizaine, jamais deux fois la même. */
const ListePhrases = z
    .array(PhraseDeBloc)
    .min(12)
    .refine((l) => new Set(l.map((t) => t.toLowerCase())).size === l.length, {
    message: "les phrases d'une même colonne doivent être distinctes",
});
/**
 * Écriture — « Le script qui tombe » (⟦C52 · A-170 §1⟧, SPEC-UI §5.1 remplacée).
 *
 * L'id de contenu reste `fil_rouge` (l'enum fermé `MinigameId` et les assists des compétences s'y
 * accrochent) ; le jeu, lui, a changé deux fois : jeu de mémoire (⟦C36⟧), puis blocs-phrases qui
 * tombent. Des **blocs** portant une phrase tombent un par un dans un plateau de colonnes (Accroche,
 * Histoire, Chute, Appel), le joueur pousse chacun dans la bonne ; la chute accélère pendant la
 * manche (`chute_depart_ms` vers `chute_fin_ms`) et d'un palier à l'autre. Plus tard : des blocs
 * pièges à laisser filer, des doublets (deux blocs à l'écran), une quatrième colonne.
 *
 * ⟦C56 · A-173⟧ La montée n'est plus une droite : la vitesse de chute suit une **courbe d'ease-in**
 * (`courbe_exposant`), calme au début et frénétique sur le dernier tiers, le dernier bloc tombe au
 * moins trois fois plus vite que le premier, et le souffle entre deux blocs raccourcit avec
 * (`pause_ms` au début, `pause_fin_ms` à la fin).
 *
 * Les tables `*_par_palier` sont indexées E0..E5, tirées vers la colonne suivante à mesure que la
 * chaîne avance dans son palier (⟦C47⟧). `duree_*_ms` est la durée visée de la manche : le dernier
 * bloc a touché le sol avant (brief C52 : 30 à 45 secondes). Les phrases sont dans `ecriture.json`.
 */
export const AtelierFilRouge = z
    .object({
    duree_longue_ms: z.number().int().min(30_000).max(45_000),
    duree_short_ms: z.number().int().min(30_000).max(45_000),
    /** La durée de chute (ms) du premier bloc, et du dernier : elle raccourcit, la vitesse monte. */
    chute_depart_ms: AtelierValeur,
    chute_fin_ms: AtelierValeur,
    /**
     * ⟦C56⟧ L'exposant de la courbe de montée (≥ 1) : la vitesse de chute vaut `1 + (R - 1) × w` fois
     * celle du départ, `w = avancement^exposant`, `R = chute_depart_ms / chute_fin_ms`. 1 = ligne
     * droite ; 2 et plus = calme au début, frénétique à la fin.
     */
    courbe_exposant: z.number().min(1).max(4),
    /** Le souffle entre la chute d'un bloc et l'apparition du suivant, au début de la manche. */
    pause_ms: z.number().int().min(0).max(3_000),
    /** ⟦C56⟧ Le même souffle à la fin de la manche (par palier E0..E5) : il raccourcit avec la montée. */
    pause_fin_ms: AtelierValeur,
    /** 3 colonnes au début, 4 plus tard. */
    colonnes_par_palier: z.array(z.number().int().min(3).max(4)).length(6),
    /** Blocs pièges (« pub douteuse ») et doublets, par palier, pour une manche longue. */
    pieges_par_palier: z.array(z.number().int().min(0).max(6)).length(6),
    doublets_par_palier: z.array(z.number().int().min(0).max(6)).length(6),
})
    .strict();
/**
 * ⟦C52 · A-170 §1⟧ **`content/ecriture.json`** : les phrases des blocs, dans l'univers parodique. Un
 * fichier à part de `atelier.json`, et ce n'est pas un détail : `atelier.json` porte le réglage du
 * serveur (tables de difficulté) et le client ne l'importe PAS exprès (`parite-client.test.ts`).
 * Les phrases, elles, sont des mots que le joueur lit : le client les importe (le bac à sable en a
 * besoin pour jouer hors compte), comme il importe `events.json`.
 *
 * Quatre colonnes de phrases (une par partie de la vidéo) et les blocs pièges (« pub douteuse »).
 */
export const EcritureFile = z
    .object({
    phrases: z
        .object({
        accroche: ListePhrases,
        histoire: ListePhrases,
        chute: ListePhrases,
        appel: ListePhrases,
        pieges: ListePhrases,
    })
        .strict(),
})
    .strict();
/** Tournage — « La bonne prise » (SPEC-UI §5.2). */
export const AtelierBonnePrise = z
    .object({
    fenetre_ms: AtelierValeur,
    traversee_ms: AtelierValeur,
    prises_longue: z.number().int().positive(), // 6
    prises_short: z.number().int().positive(), // 3 (short express)
    ecart_prises_ms: z.number().positive(), // 1200
    retake_energie: z.number().int().min(0), // 2 ⚡ (arbitrage A3)
    retake_max_par_prise: z.number().int().min(0), // 2 ✎
})
    .strict();
/**
 * ⟦C38 · A-161 §3⟧ Une piste du catalogue du Montage — le jumeau serveur du manifeste audio
 * (`client/public/audio/manifest.json`, bloc `atelier_montage_XX`). `bpm` est le tempo NATIF
 * mesuré par `scripts/beatmap.ts`, recopié tel quel : c'est lui qui décide, pour un `bpm` de
 * pattern donné, quelles pistes entrent dans le tirage (`server/src/atelier/patterns.ts`,
 * étirement sous `MONTAGE_ETIREMENT_MAX`).
 */
export const AtelierCouperRythmePiste = z
    .object({
    id: z.string().min(1),
    bpm: z.number().positive(),
})
    .strict();
/** Montage — « Couper dans le rythme » (SPEC-UI §5.3). */
export const AtelierCouperRythme = z
    .object({
    bpm: AtelierValeur,
    fenetre_rythmique_ms: AtelierValeur,
    cuts_longue: AtelierValeur,
    cuts_short: AtelierValeur,
    duree_longue_ms: z.number().positive(), // 30000
    duree_short_ms: z.number().positive(), // 20000
    preview_beats_ms: z.number().positive(), // 1200
    preview_beats_assist_ms: z.number().positive(), // 2500 (Sound design qui claque) ✎
    /** ⟦C38⟧ Le catalogue des pistes tirables — au moins une, dix acceptées sans retouche. */
    pistes: z.array(AtelierCouperRythmePiste).min(1),
})
    .strict();
/** Miniature + Titre — « Le putaclic-o-mètre » (SPEC-UI §5.4). */
export const AtelierPutaclicOMetre = z
    .object({
    periode_ms: AtelierValeur,
    zone_lumiere_neutre_pts: AtelierValeur,
    zone_chaos_pts: AtelierValeur,
    arrets_base: z.number().int().positive(), // 1 (2 avec Plume d'or)
    zone_centre_min: z.number().min(0).max(100), // 40 — centre seedé dans [40 ; 75]
    zone_centre_max: z.number().min(0).max(100), // 75
    auto_stop_oscillations: z.number().int().positive(), // 6 ✎
})
    .strict();
/** Promotion — « La Découpe » (SPEC-UI §5.5, longues seulement). */
export const AtelierLaDecoupe = z
    .object({
    chrono_ms: AtelierValeur,
    zones_chaudes: AtelierValeur,
    clips_base: z.number().int().positive(), // 3
    clips_avec_assist: z.number().int().positive(), // 4 (Merch ou Agent, non cumulable)
    longueur_clip: z.number().positive().max(1), // 0.10 = 10 % de la timeline
    zone_largeur_min: z.number().positive().max(1), // 0.06
    zone_largeur_max: z.number().positive().max(1), // 0.12
    zone_marge_min: z.number().min(0).max(1), // 0.03 ✎
    intensites: z.array(z.number().positive()).min(1), // {0,5 ; 0,7 ; 1,0}
    energie_par_clip: z.number().int().min(0), // 2 ⚡ (0 avec Usine à shorts)
    plateformes: z
        .array(z
        .object({
        id: PlatformId,
        label: z.string().min(1),
        affinite: HeatZoneType,
    })
        .strict())
        .length(3),
})
    .strict();
/**
 * `content/atelier.json` — arbitrage A11 (7e fichier de `content/`) et A12 (les valeurs
 * sont celles de la table SPEC-UI §4.2, pas celles de SPEC-API §12.19). Les 5 mini-jeux
 * sont OBLIGATOIRES : les clés reprennent l'enum fermé `MinigameId`.
 * Le fichier lui-même est écrit en M2.8 — ici, seul le schéma.
 */
export const AtelierFile = z
    .object({
    scaling: AtelierScalingRules,
    minijeux: z
        .object({
        fil_rouge: AtelierFilRouge,
        bonne_prise: AtelierBonnePrise,
        couper_rythme: AtelierCouperRythme,
        putaclic_o_metre: AtelierPutaclicOMetre,
        la_decoupe: AtelierLaDecoupe,
    })
        .strict(),
})
    .strict();
// ─────────────────────────────────────────────────────────────────────────────
// montage-patterns.json — les patterns du Montage, calés sur la musique ⟦C50 · A-169 §2⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦C50 · A-169 §2⟧ **Les patterns du Montage sont calculés HORS LIGNE**, sur l'audio, par
 * `scripts/montage-patterns.py`. Matt, 01/10/2026 : *« Crée plein de patterns différents de vitesse
 * et de nombre, mais fais-les calés sur la musique dès le départ, comme ça tu sélectionnes en random
 * parmi ceux calculés. »* Le serveur n'en tire qu'un (seedé) ; il ne calcule plus aucun carré.
 *
 * ⟦C54 · A-171⟧ **Version 2** : une manche de DURÉE FIXE (celle d'`atelier.json`), et un nombre de coups qui
 * est l'un des PALIERS `8, 12, 16, 20, 24, 30`. Plus de coups, c'est une SUBDIVISION plus serrée (`temps`,
 * `demi`, `quart`), jamais une manche plus longue. Matt, 01/10/2026 : *« Huit c'est le minimum, des fois
 * douze, vingt, trente. Et trente, ça ne veut pas dire que la musique est plus longue : on cherche à taper
 * sur un beat plus serré. »* La `vitesse` d'un pattern (lente, moyenne, rapide) n'existe plus : la durée est
 * fixe, donc le nombre de coups fixe l'écart moyen.
 *
 * Un morceau porte la liste de ses **attaques audibles** (instants du fichier, en ms) et des patterns qui ne
 * contiennent que des INDICES dans cette liste : chaque carré est donc, par construction, sur une attaque.
 * `origineMs` est l'instant du fichier qui est le zéro du jeu.
 */
export const MontageFormat = z.enum(["short", "longue"]);
export const MontageVariante = z.enum(["classique", "doubles", "contretemps"]);
/** La plus fine subdivision de la grille de tempo qu'un pattern utilise : le temps, le demi-temps, le quart de temps. */
export const MontageSubdivision = z.enum(["temps", "demi", "quart"]);
export const MontagePattern = z
    .object({
    format: MontageFormat,
    subdivision: MontageSubdivision,
    variante: MontageVariante,
    /** L'instant du fichier qui est le zéro du jeu : un temps de la grille du morceau. */
    origineMs: z.number().int().nonnegative(),
    /** L'écart médian entre deux carrés, en ms : une information, le jeu ne s'en sert pas. */
    ecartMs: z.number().int().positive(),
    /** Des indices dans `attaques` du morceau, strictement croissants (donc dans le temps). Leur nombre est un palier. */
    cuts: z.array(z.number().int().nonnegative()).min(2),
    /** Indices (dans `cuts`) des carrés qui sont le SECOND d'une paire (variante `doubles`). */
    doubles: z.array(z.number().int().nonnegative()).min(1).optional(),
    /** Indices (dans `cuts`) des carrés posés entre deux temps (variante `contretemps`). */
    contretemps: z.array(z.number().int().nonnegative()).min(1).optional(),
})
    .strict();
export const MontagePatternsPiste = z
    .object({
    /** Le même id que le manifeste audio (`client/public/audio/manifest.json`). */
    id: z.string().min(1),
    bpm: z.number().positive(),
    beatMs: z.number().positive(),
    /** Position (ms) du PREMIER temps de la grille, lue sur l'audio (jamais supposée nulle). */
    decalageMs: z.number(),
    /** Part des temps de la grille qui tombent sur une attaque audible : sous 0,60, le morceau n'a pas de pattern. */
    fiabiliteGrille: z.number().min(0).max(1),
    pulsation: z.boolean(),
    /** Le décompte (3 temps) : l'origine de tout pattern est au moins là. */
    decompteMs: z.number().int().positive(),
    dureeMs: z.number().int().positive(),
    /** Les attaques audibles du morceau, en ms, croissantes. */
    attaques: z.array(z.number().int().nonnegative()).min(8),
    /** Indices (dans `attaques`) des attaques FRANCHES : les manches de 24 et 30 coups ne posent que sur elles. */
    franches: z.array(z.number().int().nonnegative()),
    patterns: z.array(MontagePattern).min(1),
})
    .strict();
export const MontagePatternsFile = z
    .object({
    version: z.literal(2),
    genere_par: z.string().min(1),
    parametres: z
        .object({
        /** Un coup tombe sur une attaque audible à moins de ce délai (30 ms). */
        tolerance_attaque_ms: z.number().positive(),
        /** Montée de niveau minimale d'une attaque audible, en dB (mesure de C50). */
        montee_min_db: z.number().positive(),
        ecart_min_ms: z.number().positive(),
        ecart_max_ms: z.number().positive(),
        premier_cut_ms: z.tuple([z.number().positive(), z.number().positive()]),
        queue_ms: z.number().positive(),
        /** Le dernier coup tombe dans les `fenetre_dernier_ms` qui précèdent la queue : la durée de la manche est fixe. */
        fenetre_dernier_ms: z.number().positive(),
        decompte_temps: z.number().positive(),
        /** Les nombres de coups possibles, croissants. */
        paliers: z.array(z.number().int().positive()).min(2),
        /** À partir de ce nombre de coups, un pattern ne pose que sur des attaques franches. */
        franches_des: z.number().int().positive(),
        /** Un morceau est jouable (entre dans le tirage) s'il a un pattern classique pour chaque palier jusqu'à celui-ci ; au-delà il joue son maximum. */
        palier_commun_max: z.number().int().positive(),
    })
        .strict(),
    /** Les nombres de coups de `content/atelier.json` par niveau E0..E5, recopiés à la génération. */
    niveaux: z.object({ short: z.array(z.number().int().positive()).length(6), longue: z.array(z.number().int().positive()).length(6) }).strict(),
    /** La durée FIXE d'une manche, par format (`duree_short_ms`, `duree_longue_ms` d'`atelier.json`). */
    duree_ms: z.object({ short: z.number().positive(), longue: z.number().positive() }).strict(),
    pistes: z.array(MontagePatternsPiste).min(1),
    /** Les morceaux du manifeste qui n'ont pas de pattern, et pourquoi (jamais un silence sur ce choix). */
    ecartees: z.array(z.object({ id: z.string().min(1), raison: z.string().min(1) }).strict()),
})
    .strict()
    .superRefine((f, ctx) => {
    const signaler = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    const paliers = new Set(f.parametres.paliers);
    for (const format of ["short", "longue"]) {
        f.niveaux[format].forEach((n, E) => {
            if (!paliers.has(n))
                signaler(["niveaux", format, E], `${n} coups n'est pas un palier (${f.parametres.paliers.join(", ")})`);
        });
    }
    const ids = new Set();
    f.pistes.forEach((piste, ip) => {
        if (ids.has(piste.id))
            signaler(["pistes", ip, "id"], `piste « ${piste.id} » en double`);
        ids.add(piste.id);
        piste.attaques.forEach((t, i) => {
            if (i > 0 && t <= piste.attaques[i - 1])
                signaler(["pistes", ip, "attaques", i], "les attaques doivent être strictement croissantes");
        });
        const franches = new Set(piste.franches);
        piste.franches.forEach((i, k) => {
            if (i >= piste.attaques.length)
                signaler(["pistes", ip, "franches", k], "indice d'attaque hors de la liste");
        });
        piste.patterns.forEach((p, k) => {
            const chemin = ["pistes", ip, "patterns", k];
            const temps = p.cuts.map((i) => piste.attaques[i]);
            if (temps.some((t) => t === undefined)) {
                signaler([...chemin, "cuts"], `indice d'attaque hors de la liste (${piste.attaques.length} attaques)`);
                return;
            }
            const t = temps;
            if (!paliers.has(p.cuts.length))
                signaler([...chemin, "cuts"], `${p.cuts.length} coups n'est pas un palier (${f.parametres.paliers.join(", ")})`);
            for (let i = 1; i < t.length; i++) {
                if (t[i] - t[i - 1] < f.parametres.ecart_min_ms) {
                    signaler([...chemin, "cuts", i], `deux carrés à moins de ${f.parametres.ecart_min_ms} ms`);
                    break;
                }
                if (t[i] - t[i - 1] > f.parametres.ecart_max_ms) {
                    signaler([...chemin, "cuts", i], `deux carrés à plus de ${f.parametres.ecart_max_ms} ms`);
                    break;
                }
            }
            const premier = t[0] - p.origineMs;
            if (premier < f.parametres.premier_cut_ms[0] - 1 || premier > f.parametres.premier_cut_ms[1] + 1) {
                signaler([...chemin, "origineMs"], `le premier carré tombe ${premier} ms après l'origine (attendu ${f.parametres.premier_cut_ms.join("–")})`);
            }
            if (p.origineMs < piste.decompteMs)
                signaler([...chemin, "origineMs"], "origine avant la fin du décompte");
            // La durée de la manche est FIXE : le dernier coup tombe dans une fenêtre étroite avant la queue.
            const duree = f.duree_ms[p.format];
            const dernier = t[t.length - 1] - p.origineMs;
            const plafond = duree - f.parametres.queue_ms;
            if (dernier > plafond + 1 || dernier < plafond - f.parametres.fenetre_dernier_ms - 1) {
                signaler([...chemin, "cuts"], `le dernier carré tombe ${dernier} ms après l'origine (attendu ${plafond - f.parametres.fenetre_dernier_ms}–${plafond}, durée fixe ${duree} ms)`);
            }
            if (p.origineMs + duree > piste.dureeMs + 1)
                signaler([...chemin, "origineMs"], "la musique s'arrête avant la fin de la manche");
            if (p.cuts.length >= f.parametres.franches_des && p.cuts.some((i) => !franches.has(i))) {
                signaler([...chemin, "cuts"], `un carré sur une attaque non franche à partir de ${f.parametres.franches_des} coups`);
            }
            for (const cle of ["doubles", "contretemps"]) {
                for (const i of p[cle] ?? [])
                    if (i >= p.cuts.length)
                        signaler([...chemin, cle], `${cle} désigne le carré ${i}, hors du pattern`);
            }
            if (p.variante === "doubles" && !p.doubles)
                signaler([...chemin, "doubles"], "variante doubles sans aucun double");
            if (p.variante === "contretemps" && !p.contretemps)
                signaler([...chemin, "contretemps"], "variante contretemps sans aucun contretemps");
            if (p.variante !== "doubles" && p.doubles)
                signaler([...chemin, "doubles"], "des doubles dans une variante qui n'en est pas une");
            if (p.variante !== "contretemps" && p.contretemps)
                signaler([...chemin, "contretemps"], "des contretemps dans une variante qui n'en est pas une");
        });
    });
});
// ─────────────────────────────────────────────────────────────────────────────
// onboarding.json — le mode découverte ⟦A-9⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Une leçon du **mode découverte** ⟦A-9⟧ : *« le jeu s'arrête, fond opaque, et une pop-up
 * t'explique le truc avant que ça démarre »*. Ce n'est pas un habillage — A-9 en fait la
 * colonne vertébrale du début, et l'ordre des leçons EST l'ordre de l'arc 1.
 *
 * **Pourquoi c'est du contenu et pas du code** : les textes doivent pouvoir être réécrits
 * sans redéployer le client, et surtout sans passer par une session moteur. `ancre` est le
 * seul champ que l'UI interprète : il désigne **ce que la pop-up montre du doigt**. Le moteur
 * n'en fait rien du tout — il valide, il n'exécute pas.
 *
 * Le fichier est **optionnel** au boot, exactement comme `atelier.json` ⟦A11⟧ : son absence
 * ne doit pas empêcher un serveur de démarrer, sinon un contenu en cours d'écriture bloque la
 * prod.
 */
export const OnboardingLecon = z
    .object({
    id: Id,
    /** Rang dans le programme de l'arc 1 — unique et ≥ 1 (invariant vérifié au boot). */
    ordre: z.number().int().min(1),
    /** Ce que la pop-up désigne à l'écran. Libre côté contenu : l'UI y répond ou l'ignore. */
    ancre: Id,
    titre: z.string().min(1),
    texte: z.string().min(1),
})
    .strict();
export const OnboardingFile = z.array(OnboardingLecon).min(1);
// ─────────────────────────────────────────────────────────────────────────────
// Récapitulatif des fichiers de content/ (arbitrage A11 + onboarding ⟦A-9⟧)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// comments.json — le Fil ⟦SPEC-FIL §8.2⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Tonalité d'un commentaire — vocabulaire FERMÉ (SPEC-FIL §1.1).
 *
 * ⚠️ **Nommé `TonaliteId` et pas `TonaliteCommentaire`** : SPEC-FIL §8.2 écrit le second, mais
 * `engine/types.ts` porte déjà ce nom pour le type du moteur, et le barrel `shared/src/index.ts`
 * ré-exporte les deux modules. Même frontière de nommage que `GenreId` (contenu) vs
 * `GenreAvatar` (moteur) : `content/` ne dépend pas du moteur, c'est le moteur qui lit le
 * contenu. Les VALEURS, elles, sont rigoureusement les mêmes — c'est ce qui compte.
 */
export const TonaliteId = z.enum([
    "sympa",
    "question",
    "critique",
    "troll",
    "toxique",
    "star",
]);
/** Les 5 réponses de PLAN §4.14 — vocabulaire FERMÉ (SPEC-FIL §4). Même note de nommage. */
export const ReponseFilId = z.enum(["coeur", "humour", "epingler", "ignorer", "supprimer"]);
/**
 * Ce qu'un commentaire porte en plus de son texte (SPEC-FIL §5). Absent = aucun cadeau.
 *
 * `hook_evenement.event_id` **DOIT** exister dans `events.json` — invariant croisé vérifié au
 * boot (`load.ts`) et par le test de cohérence T-FIL-C1.
 */
export const CadeauCommentaire = z.discriminatedUnion("type", [
    z.object({ type: z.literal("idee") }).strict(),
    z.object({ type: z.literal("hook_evenement"), event_id: Id }).strict(),
    z.object({ type: z.literal("verbatim") }).strict(),
]);
/**
 * Conditions d'apparition d'un commentaire (SPEC-FIL §8.2). Toutes optionnelles : un
 * commentaire sans condition est **toujours** candidat, ce qui est le cas le plus fréquent.
 *
 * `apres_publication`, `classe_video` et `format_video` ne peuvent passer qu'à une arrivée
 * **de publication** : à la nuit, il n'y a pas de vidéo dont parler.
 */
export const CommentaireConditions = z
    .object({
    subs_min: z.number().int().min(0).optional(),
    subs_max: z.number().int().min(0).optional(),
    image_min: z.number().min(-100).max(100).optional(),
    image_max: z.number().min(-100).max(100).optional(),
    hype_min: z.number().min(0).max(100).optional(),
    hype_max: z.number().min(0).max(100).optional(),
    /** `true` = n'arrive qu'après une publication (jamais à la nuit). */
    apres_publication: z.boolean().optional(),
    classe_video: z.enum(["trend", "standard", "evergreen", "nulle"]).optional(),
    format_video: z.enum(["short", "longue"]).optional(),
    flags_requis: z.array(Id).default([]),
    flags_interdits: z.array(Id).default([]),
})
    .strict();
/**
 * Un commentaire du Fil — **7ᵉ fichier éditorial** de `content/` (SPEC-FIL §8.1).
 *
 * Mêmes règles que les six autres : ids `^[a-z0-9_]+$`, textes en français, ton complice
 * YouTube FR, **jamais** de marque réelle ni le nom / visage / catchphrase exacts de la
 * personne dont l'univers est parodié (CLAUDE.md, test T14) — les verbatims de la KB sont
 * **renommés** (Castelclic, la Mamie, les Clicks).
 *
 * ⚠️ **Le lieu le plus exposé au dérapage du jeu.** Un commentaire est bienveillant, drôle ou
 * vrai — jamais moqueur, et le stéréotype vient toujours de l'extérieur, jamais de la voix du
 * jeu. Les entrées `toxique` se relisent **avec Mathieu avant merge**, même règle que les
 * événements genrés ⟦A-4 H⟧ : on montre la réalité, on ne la reproduit pas.
 */
export const Commentaire = z
    .object({
    id: Id,
    /** Pseudo parodique de l'auteur. Écrasé à l'affichage par le nom du PNJ si `pnj`. */
    auteur: z.string().min(1),
    /** C'est un commentaire, pas un roman. */
    texte: z.string().min(1).max(280),
    tonalite: TonaliteId,
    /** Échelle 1-30, comme les événements. */
    poids: z.number().int().positive().max(30),
    cadeau: CadeauCommentaire.optional(),
    /** `true` ⇒ l'auteur est un **PNJ persistant** ⟦A-4 D⟧ (⇔ `tonalite === "star"`). */
    pnj: z.boolean().default(false),
    conditions: CommentaireConditions.default({ flags_requis: [], flags_interdits: [] }),
    /**
     * Réponses proposées au joueur. Défaut = les 5. Permet un commentaire qu'on ne peut pas
     * supprimer (celui d'un gros créateur), ou qu'on ne peut qu'ignorer.
     */
    reponses: z.array(ReponseFilId).min(1).default(["coeur", "humour", "epingler", "ignorer", "supprimer"]),
    /** Réplique affichée quand la vanne passe (§4.3). Contenu, jamais moteur. */
    reponse_humour_reussie: z.string().max(280).optional(),
    /** Réplique affichée quand la vanne ne passe pas (§4.3). */
    reponse_humour_ratee: z.string().max(280).optional(),
})
    .strict();
export const CommentsFile = z.array(Commentaire).min(1);
// ─────────────────────────────────────────────────────────────────────────────
// §3.x axe-produit.json ⟦SPEC-AXE-PRODUIT §1.1 · §9.2 question 1 / A-62⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * **Les mots de l'axe Produit ↔ Authentique** — et rien que les mots.
 *
 * Le moteur calcule un nombre entre −100 et +100 et une bande ; il n'écrit **aucun texte de
 * lore** (SPEC-MOTEUR §9.2). Ce fichier porte ce que le joueur LIT : le libellé de chaque
 * bande, et la phrase de chacune des deux dérives.
 *
 * 🔑 **Pourquoi un fichier et pas trois chaînes dans le composant.** « Contenu = données »
 * (CLAUDE.md) : le jour où Mathieu veut que la bande haute se dise « chaîne industrielle »
 * plutôt que « chaîne produite », c'est une édition de contenu, pas un patch de client. Et
 * les deux portes de rendu — serveur (`content/load.ts`) et bac à sable (`client/src/game/
 * content.ts`) — lisent le même fichier, ce que le test de parité ⟦A-32⟧ vérifie.
 *
 * ⚠️ **Ce fichier ne porte AUCUN événement.** Les ≥ 13 événements de SPEC-AXE-PRODUIT §6.4
 * vivent dans `content/events.json` comme tous les autres, avec `produit_min` / `produit_max`
 * ou les drapeaux `derive_produit` / `derive_artisanale` en conditions. Ils n'existent pas
 * encore : c'est la raison pour laquelle l'axe est livré éteint.
 */
export const BandeProduitId = z.enum(["artisanal", "hybride", "produit"]);
export const DeriveProduitId = z.enum(["produit", "artisanale"]);
export const AxeProduitFile = z
    .object({
    /** Les trois bandes de §1.1, dans l'ordre de l'axe. Les trois sont obligatoires. */
    bandes: z
        .array(z
        .object({
        id: BandeProduitId,
        icone: z.string().min(1),
        /** Ce que le joueur lit sur sa fiche de chaîne : « chaîne artisanale ». */
        libelle: z.string().min(1).max(60),
        description: z.string().min(1).max(280),
    })
        .strict())
        .length(3),
    /**
     * Les deux dérives de §5. ⚠️ **Aucune des deux n'a de vainqueur moral** — c'est le pendant,
     * pour cet axe, de la règle anti-caricature d'⟦A-4 H⟧ : le jeu montre le prix des deux
     * routes, il n'en récompense aucune. D'où la `morale`, qui renvoie les deux camps dos à dos.
     */
    derives: z
        .array(z
        .object({
        id: DeriveProduitId,
        icone: z.string().min(1),
        titre: z.string().min(1).max(80),
        ligne: z.string().min(1).max(280),
        morale: z.string().min(1).max(160),
    })
        .strict())
        .length(2),
})
    .strict();
/**
 * ⟦A-44 · A-71⟧ **`content/guide.json`** — le guide MeTube, faux petit journal de l'univers.
 *
 * ## Pourquoi c'est du CONTENU et pas des chaînes dans un composant
 *
 * ⟦A-43⟧ a sorti du jeu vingt phrases qui expliquaient *pourquoi* au lieu de dire *ce qui se
 * passe*, et sa règle est que **ces textes déménagent, ils ne disparaissent pas**. Leur
 * destination est ici. Les remettre en dur dans du TSX les rendrait aussi difficiles à relire
 * qu'avant — or c'est de la prose, elle se retouche, et elle se retouche sans recompiler.
 *
 * ## Ce que le fichier ne contient PAS
 *
 * ⚠️ **Aucune règle que le moteur lise.** Le guide *décrit* des mécaniques, il n'en définit
 * aucune : `EngineContent` ne le référence pas, `resolve()` ne le reçoit pas, et un
 * `guide.json` absent ne change pas une partie d'un caractère. C'est voulu — un guide qui
 * pourrait déséquilibrer le jeu serait un guide qu'on n'oserait plus écrire.
 *
 * ⚠️ **Les chiffres qu'il cite sont recopiés, donc datés.** Le brouillon
 * (`docs/GUIDE-METUBE-BROUILLON.md`) pose la question et la tranche ici : le **jeu** lit ses
 * constantes vivantes, le **journal** les recopie et les attribue. Un article de presse cite
 * une mesure et sa date ; il ne se met pas à jour tout seul, et personne ne s'en étonne. Le
 * champ `mesure` de chaque dossier existe pour ça : il dit d'où vient le chiffre.
 */
const GuideEncadre = z
    .object({
    titre: z.string().min(1).max(80),
    texte: z.string().min(1),
})
    .strict();
const GuideDossier = z
    .object({
    id: Id,
    /** La rubrique du journal : « Mécanique », « Dossier », « Le coin des pros »… */
    rubrique: z.string().min(1).max(40),
    titre: z.string().min(1).max(120),
    /**
     * ⟦C57 · A-174⟧ Le titre court, celui que l'écran affiche par défaut (5 mots au plus) : le long
     * (`titre`) reste la source, il s'ouvre dans « Les dessous ». Optionnel : sans lui, l'écran
     * retombe sur le titre long : un contenu antérieur au chantier se lit comme avant.
     */
    titre_court: z.string().min(1).max(60).optional(),
    /** Le chapô — le paragraphe en gras sous le titre. */
    chapo: z.string().min(1),
    /**
     * ⟦C57 · A-174⟧ Le chapô court : UNE phrase de 12 mots au plus, affichée sous le titre ; le
     * chapô entier s'ouvre dans « Les dessous ». Optionnel, même raison que `titre_court`.
     */
    chapo_court: z.string().min(1).max(120).optional(),
    paragraphes: z.array(z.string().min(1)).min(1),
    /** L'encadré : le tableau ou la mise en garde qu'on lit en diagonale. */
    encadre: GuideEncadre.optional(),
    /**
     * D'où viennent les chiffres de ce dossier. Obligatoire dès qu'il y en a un — c'est ce qui
     * distingue une mesure d'une impression, et ce qui permettra de rejouer la mesure le jour
     * où elle aura vieilli.
     */
    mesure: z.string().min(1).optional(),
})
    .strict();
export const GuideFile = z
    .object({
    titre: z.string().min(1).max(60),
    sous_titre: z.string().min(1).max(120),
    /** L'ours du journal — le petit pavé d'infos légales, ici parodique. */
    ours: z.string().min(1),
    dossiers: z.array(GuideDossier).min(1),
})
    .strict();
export const CONTENT_FILES = [
    "meta.json",
    "skills.json",
    "events.json",
    "milestones.json",
    "trends.json",
    "subjects.json",
    "atelier.json",
    "onboarding.json",
];
//# sourceMappingURL=schema.js.map